/* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-07-21) — Adjust Order panel.
   Admin-only DD surface for correcting a PAID online order (line qtys + freight)
   that nets money in either direction. Worker: /order-adjust/* (rfq-worker).
   Spec: Team-Portal-Bot/tools/Quote-Dashboard/ADJUST-ORDER-PLAN.md.

   Self-contained: renders its own card (window.RQD.renderAdjustOrderCard) and
   owns its modal + all endpoint calls (window.RQD.adjustOrder.*). dashboard-main.js
   only (a) calls the card renderer in buildDetailHtml and (b) delegates
   data-adjust-action clicks here. Loads before dashboard-main.js.

   PHASE 1: order-edit rail only (refund / even swap). A net-COLLECT is refused by
   the worker with a pointer to the existing "Collect a balance" flow. */
(function () {
  'use strict';
  window.RQD = window.RQD || {};

  var API = null; // set on first use from window.RepPortal.API
  function api() { if (!API) API = window.RepPortal && window.RepPortal.API; return API; }

  // Render-time cache so the click handler doesn't need `q` in scope.
  var _cache = {};

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(n) { return '$' + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2); }

  /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) - a Shopify GID inside an attribute selector.
     `gid://shopify/LineItem/123` carries slashes and colons, which querySelector reads as
     syntax; CSS.escape is the browser's own answer and the raw string is the fallback for a
     context that has none (it has worked for the add-row lookup beside it since 2026-08-10). */
  function _cssEsc(v) {
    var s = String(v == null ? '' : v);
    return (window.CSS && CSS.escape) ? CSS.escape(s) : s;
  }

  /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — /product-search results, flattened to
     VARIANTS and narrowed to the words typed. Lifted out of runSearch verbatim so the Add box
     and the Swap picker share ONE definition — a second copy of this filter is how the
     roll-layout surfaces drifted apart, and it carries two hard-won rules:

     ⚠ /product-search hands the string to Shopify's full-text search, which reads the
     product DESCRIPTION — every Legend machine mentions a welded frame and guide rods, so a
     bare `weld rod` returns six Legend machines before the real ECORE weld rods. Every word
     must therefore appear in the ROW's own label (product title + variant title) or its SKU.

     ⚠ And COLOUR IS A VARIANT, so one product matching on a colour option flattens to all
     of its colours; `Catalina` used to list nineteen colours that are not Catalina.

     ⛔ IT FALLS BACK RATHER THAN SHOWING NOTHING. A filter that can return an empty list is
     worse than the noise it replaces, and `fellBack` is keyed on the fallback ACTUALLY
     happening rather than on "the filter removed something" — when every row legitimately
     matches, a did-it-narrow flag would claim nothing matched. */
  function variantRowsFor(q, products) {
    var words = String(q || '').toLowerCase().split(/\s+/).filter(function (w) { return w; });
    function matchesAll(text) {
      var l = String(text || '').toLowerCase();
      for (var i = 0; i < words.length; i++) if (l.indexOf(words[i]) === -1) return false;
      return true;
    }
    var all = [];
    (products || []).forEach(function (p) {
      (p.variants || []).forEach(function (v) {
        var vt = (v.title && v.title !== 'Default Title') ? v.title : '';
        /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — MSRP and COST ride along. Both are
           already on every /product-search and /product-by-handle variant and were thrown away
           here; the swap row needs them to lay out like a Workstation row. `price` IS the MAP
           (the website selling price) AND the hard ceiling on what Shopify will bill an added
           line — one number, two meanings, both true. */
        all.push({ id: v.id, sku: v.sku, price: v.price, msrp: v.compareAtPrice || null,
          cost: v.cost || null, label: p.title + (vt ? ' — ' + vt : '') });
      });
    });
    var hits = all.filter(function (v) { return matchesAll(v.label) || matchesAll(v.sku); });
    return {
      rows: (hits.length ? hits : all).slice(0, 50),
      fellBack: hits.length === 0,
      total: all.length,
      words: words,
    };
  }
  // Mirror the DD's formatDate output (M/D/YYYY) for YYYY-MM-DD inputs.
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ IT USED TO SLICE THE UTC STRING.
     Ryan on SHOP-1941: *"I got notified via mailer@shopify.com on 8/27/2026 that he paid the
     balance, but the ADJUST ORDER card says the $483.20 was collected on 8/26/2026. Pretty sure
     Shopify is correct."* Shopify was.

     Two faults compounded, and this is the second of them. The old body ran a regex over the first
     ten characters of the ISO text and reformatted them, so `2026-08-26T01:04Z` printed as 8/26 —
     while in Mountain time that instant is **7:04 pm on the 25th**. Every date on this card was the
     UTC calendar day, which is a different day from the reader's for six hours out of twenty-four.

     ⚠️ Now parsed and rendered in the READER's zone, which is the rule the deal feed's own
     `_seqWhenLabel` states: *"rendered in the READER's timezone, like every other timestamp on this
     feed."* Falls back to the old slice for anything unparseable, so a malformed value still shows
     something rather than "Invalid Date". */
  function fmtDate(s) {
    var raw = String(s || '');
    if (!raw) return '';
    var d = new Date(raw);
    if (!isNaN(d.getTime())) {
      return (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    }
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    return m ? (parseInt(m[2], 10) + '/' + parseInt(m[3], 10) + '/' + m[1]) : raw;
  }

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND THE FIRST FAULT: THE DATE BESIDE A
     SETTLED ROW WAS THE DAY IT WAS RAISED, NOT THE DAY IT WAS PAID.

     SHOP-1941's $483.20 collect was created 2026-08-26T01:04Z and paid 2026-08-27T15:37Z. The row
     read "collected · 8/26" off `createdAt`, because that is the only timestamp the line has ever
     shown — fine while a row was pending (when it was raised IS the news), wrong the moment it
     settles, and the discrepancy only surfaces when somebody cross-checks Shopify's own email.

     ⚠️ THE SETTLEMENT STAMP WINS ONLY WHEN THERE IS ONE. A pending or superseded row still shows
     when it was raised — there is no settlement date to show, and inventing one would be worse
     than the bug. `paidAt` for a collect, `releasedAt` for a refund; both are written by
     handlers/order-adjust-routes.js at the moment the state flips. */
  function adjWhen(a) {
    var s = (a && a.settlement) || {};
    return s.paidAt || s.releasedAt
      || (a && a.orderEdit && a.orderEdit.committedAt) || (a && a.createdAt) || '';
  }
  function uuid() {
    return 'ao-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-05) — deal ref vs ORDER name.
     For an online row they're the same string, so the panel used `ref` for both. An OFFLINE
     deal paid by Shopify invoice has a ref that is NOT its order name (RW-1260-A holds order
     SHOP-1862), and every worker route here is keyed by ORDER name (adjustments live at
     order-adjust:{orderName}). So: cache key stays the deal ref, but anything sent as
     orderName goes through here, and the deal ref rides along separately as `ref` so the
     worker can find the lifecycle to recompute commissions on. */
  function orderNameFor(ref) {
    var c = _cache[ref];
    return (c && c.orderName) || ref;
  }
  /* No dealRefFor(ref) helper on purpose: the two places that need the deal ref (the preview
     refresh and the commit) both already hold the cache object `c` and are NOT in a scope where
     `ref` exists — reaching for a ref-keyed lookup there threw a ReferenceError under strict
     mode, synchronously, before the promise chain was attached, so the .catch never ran and the
     preview sat on "Calculating…" forever. Read c.dealRef directly. */

  /* ── Card ──────────────────────────────────────────────────────────────
     Minimal by design — a single entry button. Pending-settlement state lives
     inside the panel (fetched on open via /order-adjust/state), so the card
     stays a thin, always-safe launcher. */
  function renderAdjustOrderCard(q) {
    if (!q || q.lifecycleStatus !== 'sold') return '';
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — an OFFLINE deal paid through Shopify gets
       the card too. It owns a real order (`paidOrderGid`, stamped server-side only when a paid
       order actually exists — quote-summary-builder.js), and the panel has been able to drive one
       since 2026-08-05; what it never had was a door of its own. `!q.cancelled` mirrors the cog's
       own gate for these rows. The ONLINE branch is left exactly as it was, cancelled orders
       included — the worker refuses those at commit with "Order is cancelled", and changing a
       second thing here would hide a state somebody may be relying on seeing. */
    var _offlinePaid = q.source !== 'online' && !!q.hasPaidOrder && !!q.paidOrderGid && !q.cancelled;
    if (q.source !== 'online' && !_offlinePaid) return '';
    var ref = q.reference || '';
    _cache[ref] = {
      /* For an offline deal the tool must act on the ORDER, never on the draft behind the quote:
         `q.id` is that draft's gid and editing it is precisely what Shopify refuses once the
         customer has paid. `reference` is the ORDER name because it is what the commit's
         confirm-guard compares against, while the cache KEY stays the deal ref. Same shape the
         cog-chooser seed builds — see `open()` below, which documents why both keys exist. */
      id: _offlinePaid ? q.paidOrderGid : (q.id || ''),
      reference: _offlinePaid ? (q.paidOrderName || ref) : ref,
      orderName: _offlinePaid ? (q.paidOrderName || '') : '',
      dealRef: _offlinePaid ? ref : '',
      // Left empty for an offline deal on purpose: /order-adjust/family-state refills the lines
      // from Shopify on open, and the draft's items are not the order's items any more.
      lineItems: (!_offlinePaid && Array.isArray(q.onlineLineItems)) ? q.onlineLineItems : [],
      shipping: (q.financials && Number(q.financials.freightCharged)) || 0,
      /* CUSTOM_FREIGHTREMINDER_BY_CLAUDE (Ryan 2026-08-29) — the broker cost ALREADY RECORDED for
         this order, so the Freight "Cost" box below opens pre-filled instead of asking for a
         number the deal is already carrying.

         Ryan: *"I'll need it both directions ... Never know what order an admin might do it in.
         Can't expect them to remember the same order perfectly every time."* The commit writes
         this value back the other way (order-adjust-routes.js), so whichever box is typed in
         first, the other one agrees.

         ⚠️ `financials.freightCost` is the RESOLVED cost the Financials card shows — the sum of
         the bucket Freight cost boxes, or the per-vendor handshake, whichever the order uses. It
         is deliberately not read off one specific bucket: this box is per ORDER, and so is that
         figure. */
      freightCostSaved: (q.financials && Number(q.financials.freightCost)) || 0,
      customerEmail: q.customerEmail || (q.billTo && q.billTo.email) || '',
      customer: q.customer || '',
      fulfilled: !!(window.RQD.isOrderFullyFulfilled && window.RQD.isOrderFullyFulfilled(q)),
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — the deal's OTHER Shopify orders, as a
         HINT only. The worker resolves the family from the parent order's own tags and verifies
         every ref against the order it names, so a wrong entry here is dropped, not trusted.
         Passed at all because a balance child whose parent-side `linked-to:` tag was never
         written is invisible from the parent — and an invisible member is how the panel came to
         show 1 of SHOP-1933's 5 lines in the first place. */
      childRefs: (Array.isArray(q.balanceChildren) ? q.balanceChildren : [])
        .map(function (c) { return c && (c.orderName || c.ref); })
        .filter(Boolean),
    };
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — the ORDER-name alias, for the same reason
       the cog-chooser seed carries one: everything downstream re-keys the cache by `c.reference`
       (the post-commit reopen, refreshCardHistory, the pending settlement's data-ref), and on an
       offline deal that is the order name. Without the second key the commit succeeds and the
       reopen alerts "Order data not loaded", which reads exactly like a commit that failed. */
    if (_offlinePaid && q.paidOrderName && q.paidOrderName !== ref) {
      _cache[q.paidOrderName] = _cache[ref];
    }
    // Plain section title (stable text) so the generic collapse mechanism slugs
    // it to "adjust-order" — defaults collapsed via shouldAutoCollapse.
    // History fills async (fillHistories, wired off applyCollapseState); the
    // button sits BELOW it so it shifts down as adjustments accrue.
    return '<div class="rqd-detail__arap rqd-ao">'
      + '<div class="rqd-detail__section-title">Adjust Order</div>'
      + '<div class="rqd-ao__history" data-ref="' + esc(ref) + '"></div>'
      + '<div class="rqd-ao__card-actions">'
      + '<button type="button" class="rqd-ao__card-btn" '
      + 'data-adjust-action="open-panel" data-ref="' + esc(ref) + '">Adjust order</button>' /* Ryan 2026-10-06: sentence case */
      + '</div></div>';
  }

  /* ── On-card history log ───────────────────────────────────────────────
     Each `.rqd-ao__history[data-ref]` placeholder is filled from /order-adjust/state
     (fetched once per element, cached on the node). Shows what changed + when +
     the settlement status, newest first. */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — is this row the balance the customer is
     actually going to pay? There is ONE pay-link per order and it charges the CURRENT outstanding
     balance, so among several pending collects exactly one figure is live and the rest are history.
     `isLive` is decided by the caller (newest-first order), not here, because "newest" is a property
     of the list, not of a row. */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-18) — 'pending' EXPLICITLY, not "anything that
     isn't paid". A retired request now carries status 'superseded', and !== 'paid' would have
     counted it as live and let it win the pay-link pill. */
  function isPendingCollect(a) {
    var s = (a && a.settlement) || {};
    return s.type === 'invoice' && s.status === 'pending';
  }
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — a balance that went DOWN. Settlement type
     stays 'invoice' on purpose (see lib/order-adjust.js) so it inherits every collect behaviour —
     supersedes the previous figure, owns the pay-link, reconciles when paid. `direction` is the
     only thing that separates the two, and it changes the WORDING, never the plumbing.
     ⛔ There is no Release-refund button on one of these, and that falls out for free: the pending-
     refund list filters on type 'refund', which this never is. Nothing was paid, so nothing can be
     released. */
  function isReduced(a) {
    return !!(a && a.settlement && a.settlement.direction === 'reduced');
  }

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the rails the Commit button may act on.
     ⛔ ONE list, because there were TWO and they drifted the moment a rail was added. renderPreview
     decides whether to ENABLE the button; the click handler independently decides whether to ACT on
     it. Adding 'reduce' to the first and not the second produced the worst possible symptom: an
     enabled, correctly-labelled button that silently did nothing when Ryan pressed it, with no error
     anywhere to explain why. A guard that disagrees with the control it guards is not a guard.
     The worker keeps its own copy on purpose (never trust the client), but these two are one. */
  var COMMITTABLE_RAILS = ['order-edit', 'freight-refund', 'collect', 'refund-only', 'reduce'];
  function isCommittableRail(rail) {
    return COMMITTABLE_RAILS.indexOf(String(rail || '')) !== -1;
  }

  function histLine(a, isLive, trailingHtml) {
    var parts = (a.lines || []).map(function (l) { return esc(l.title.split(' - ')[0].split(' (')[0]) + ' ' + l.fromQty + ' → ' + l.toQty; });
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — ADDED items belong in the history line too.
       They never were, which no one noticed while every add was also a charge: the settlement said
       "collected $X" and the money told the story. A no-charge add settles at nothing, so with
       additions left out the row degraded to the bare word "adjustment" — the history of a deal
       where we gave something away would not have named the thing. */
    (a.additions || []).forEach(function (ad) {
      if (!ad) return;
      var _t = esc(String(ad.title || ad.sku || 'item').split(' - ')[0].split(' (')[0]);
      parts.push('+' + (Number(ad.quantity) || 1) + ' × ' + _t + (ad.noCharge ? ' (free)' : ''));
    });
    if (a.freight && a.freight.addAsShippingLine) parts.push('freight +' + money(a.freight.addAsShippingLine));
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — a reduce RE-PRICES freight instead of
       adding to it, so addAsShippingLine is 0 and the row would have said nothing about a freight
       change that really happened. */
    else if (a.freight && a.freight.replaceShippingLines) parts.push('freight → ' + money(a.freight.targetShippingTotal));
    var what = parts.join(', ') || (a.reason ? esc(a.reason) : 'adjustment');
    var s = a.settlement || {};
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-18) — only ONE collect on an order is ever
       actually collected. SHOP-1950 was revised three times and all three rows said "collected",
       because the worker had flipped them all to 'paid'. Retired requests now say "replaced":
       they were real figures we asked for, superseded before anyone paid them. */
    /* CUSTOM_COLLECTVOID_BY_CLAUDE (AIOPS ad52a4258e0e, 2026-09-08) — 'voided' is a request that
       will never be paid: the order was cancelled, or Shopify shows nothing outstanding and no
       payment since it was committed. SHOP-2016's unpaid freight collect read "collected" after
       the cancel because the worker mistook a zeroed balance for a payment. Say what happened;
       "collected" here is the one word that must never be wrong. */
    var voidWord = s.voidReason === 'order-cancelled' ? ' voided — order cancelled' : ' voided — balance cleared';
    var collectWord = s.status === 'paid' ? ' collected'
      : (s.status === 'superseded' ? ' replaced'
        : (s.status === 'voided' ? voidWord : ' awaiting payment'));
    var settle = s.type === 'refund'
      ? 'Refund ' + money(s.amount) + (s.status === 'released' ? ' released' : ' pending')
      /* A reduce reads as what it IS — the balance moved down — rather than as "Collect $180.77",
         which is true but tells you nothing about why the number changed. */
      : (isReduced(a)
        ? (s.type === 'none'
          ? 'Balance cleared' + (s.previousAmount ? ' (was ' + money(s.previousAmount) + ')' : '')
          : 'Balance reduced to ' + money(s.amount) + (s.previousAmount ? ' from ' + money(s.previousAmount) : '') + collectWord)
      : (s.type === 'invoice' ? 'Collect ' + money(s.amount) + collectWord : 'even'));
    /* CUSTOM_ADJUSTUNDO_BY_CLAUDE (Ryan 2026-10-06) - an adjustment taken back with Undo. The row stays, so the
       history still says it happened; it reads "undone" and is greyed. */
    var isUndone = s.status === 'undone' || !!a.undone;
    /* CUSTOM_UNDONEWORDS_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - "undone" did not say whether the customer had been
       sent anything. `a.email` is stamped by every pay-link / explanation send (the worker's sendAdjustEmail). */
    if (isUndone) {
      settle = s.type === 'invoice' ? 'Collect ' + money(s.amount) : (s.type === 'refund' ? 'Refund ' + money(s.amount) : 'Adjustment');
      settle += (s.type === 'invoice' || s.type === 'refund') ? (a.email ? ' cancelled after it was sent' : ' cancelled, never sent') : ' cancelled';
    }
    var settleDone = isUndone || s.status === 'released' || s.status === 'paid' || s.status === 'voided' || s.type === 'none';
    /* ⚠️ NO `.slice(0, 10)` ANY MORE — that is what forced the UTC calendar day. `fmtDate`
       needs the whole timestamp to place it in the reader's zone. */
    var when = fmtDate(adjWhen(a));
    var emailed = (a.email && !isUndone) ? ' · emailed' : ''; // CUSTOM_UNDONEWORDS_BY_CLAUDE - the settle text already says it was sent
    /* CUSTOM_PENDINGPAY_BY_CLAUDE (Ryan 2026-08-29) — say NOT SENT out loud on a live collect.
       Ryan: *"we've created the adjustment but not sent them anything -- in this case b/c we're
       waiting on freight still."* Until now the only signal was the ABSENCE of "· emailed", and an
       absence is not a signal: a balance created ten minutes ago and one the customer has been
       sitting on for a week looked identical.

       ⚠️ ONLY ON THE LIVE COLLECT. A superseded row was never going to be sent — it was retired
       before anyone emailed it — so badging it "not sent" would read as an outstanding task. A
       paid or released row is finished. `isLive && isPendingCollect` is exactly the one row that
       represents money we intend to ask for and have not yet asked for. */
    var notSent = (!a.email && isLive && isPendingCollect(a) && !settleDone)
      ? '<span class="rqd-ao__hist-unsent" title="The adjustment is committed and the pay-link exists, but nothing has been emailed to the customer yet.">not sent</span>'
      : '';
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — three states, not two:
         done            → green text, unchanged
         live collect    → dark-green PILL (the amount they will actually pay)
         superseded      → dark-green BOLD text (a real figure, but no longer owed)
       The old code had only done/pending, so every pending collect shouted equally in amber and
       nothing said which one the pay-link is currently set to. `emailed` stays OUTSIDE the pill —
       it is metadata about the row, not part of the amount. */
    var settleCls = (isUndone || s.status === 'superseded' || s.status === 'voided') ? 'rqd-ao__hist-superseded'
      : settleDone ? 'rqd-ao__hist-done'
      : (isPendingCollect(a) ? (isLive ? 'rqd-ao__hist-live' : 'rqd-ao__hist-superseded')
        : 'rqd-ao__hist-pending');
    var settleHtml = (settleDone || !isPendingCollect(a) || !isLive)
      ? '<span class="rqd-ao__hist-settle ' + settleCls + '">' + esc(settle) + emailed + '</span>'
      : '<span class="' + settleCls + '">' + esc(settle) + '</span>' + notSent
        + '<span class="rqd-ao__hist-settle">' + esc(emailed) + '</span>';
    // Inline flow, date last after " - " (mirrors the AR/AP payments line).
    var inner = '<span class="rqd-ao__hist-what">' + what + '</span>'
      + '<span class="rqd-ao__hist-sep"> · </span>'
      + settleHtml
      + '<span class="rqd-ao__hist-when"> - ' + esc(when) + '</span>';
    var rowCls = 'rqd-ao__hist-row' + (isUndone ? ' rqd-ao__hist-row--undone' : '');
    /* CUSTOM_ADJUSTUNDO_BY_CLAUDE (Ryan 2026-10-06) - the Undo button rides on the row with the pill, right-aligned
       like Adjust Order. The text is wrapped so the row can be a flex line without losing the spaces in " · ". */
    if (trailingHtml) return '<div class="' + rowCls + ' rqd-ao__hist-row--undo"><span class="rqd-ao__hist-main">' + inner + '</span>' + trailingHtml + '</div>';
    return '<div class="' + rowCls + '">' + inner + '</div>';
  }

  /* CUSTOM_ADJUSTCOMMIT_BY_CLAUDE (AIOPS suggestion 729ade64f389, Ryan 2026-09-04) — an agent's
     commit that is parked on a hold (window or approval) is not in the ledger yet, because nothing
     has been committed. It rides on /order-adjust/state as `held` and is drawn here ABOVE the
     history, so an admin opening the card sees "AIOPS wants to charge $179.30 freight, commits at
     2:34 PM" rather than a blank card that is about to change under them. Release and discard
     live on the AI Agents page; this row only points there. */
  function heldLine(h) {
    var when;
    if (h.holdMode === 'approval') when = 'awaiting approval on the AI Agents page';
    else if (h.sendAfter) {
      var ms = new Date(h.sendAfter).getTime() - Date.now();
      when = ms <= 0 ? 'commits on the next tick' : (ms < 3600000 ? 'commits in ' + Math.max(1, Math.round(ms / 60000)) + ' min' : 'commits at ' + fmtDate(h.sendAfter));
      when += ' unless discarded on the AI Agents page';
    } else when = 'held';
    return '<div class="rqd-ao__hist-row rqd-ao__hist-row--held">'
      + '<span class="rqd-ao__hist-what">' + esc(h.heldForAgent || 'agent') + ' proposed: ' + esc(h.summary || 'an adjustment') + '</span>'
      + '<span class="rqd-ao__hist-sep"> · </span>'
      + '<span class="rqd-ao__hist-pending" title="Nothing has changed on the order yet. The numbers are recomputed at commit and it is dropped if the order moved.">held — ' + esc(when) + '</span>'
      + '</div>';
  }

  /* CUSTOM_ADJUSTEMAIL_BY_CLAUDE (AIOPS suggestion 0de02a43dc8e, Ryan 2026-09-04) — an agent's
     customer pay-link email that is parked on a hold. It rides on /order-adjust/state as
     `heldEmail` (no body — the Approvals page carries that) and is drawn beside a held commit, so
     an admin opening the card sees "AIOPS queued the pay-link email to pat@x.com, sends at 2:34 PM"
     instead of a balance that reads "not sent" and then, minutes later, "· emailed" with nothing
     in between. Release and discard live on the AI Agents page; this row only points there. */
  function heldEmailLine(h) {
    var when;
    if (h.holdMode === 'approval') when = 'awaiting approval on the AI Agents page';
    else if (h.sendAfter) {
      var ms = new Date(h.sendAfter).getTime() - Date.now();
      when = ms <= 0 ? 'sends on the next tick' : (ms < 3600000 ? 'sends in ' + Math.max(1, Math.round(ms / 60000)) + ' min' : 'sends at ' + fmtDate(h.sendAfter));
      when += ' unless discarded on the AI Agents page';
    } else when = 'held';
    return '<div class="rqd-ao__hist-row rqd-ao__hist-row--held">'
      + '<span class="rqd-ao__hist-what">' + esc(h.heldForAgent || 'agent') + ' queued: ' + esc(h.summary || 'the pay-link email') + '</span>'
      + '<span class="rqd-ao__hist-sep"> · </span>'
      + '<span class="rqd-ao__hist-pending" title="Nothing has been sent yet. The balance and pay-link are re-read from Shopify at release and the email is dropped if either moved.">held — ' + esc(when) + '</span>'
      + '</div>';
  }

  function renderHistoryInto(el, adjustments, held, heldEmail, undo) {
    var heldRows = (held || []).map(heldLine).join('') + (heldEmail || []).map(heldEmailLine).join('');
    if ((!adjustments || !adjustments.length) && !heldRows) { el.innerHTML = ''; el.classList.remove('rqd-ao__history--has'); return; }
    if (!adjustments || !adjustments.length) {
      el.innerHTML = '<div class="rqd-ao__history-title">Adjustment history</div>' + heldRows;
      el.classList.add('rqd-ao__history--has');
      autoExpandCard(el);
      return;
    }
    var sorted = adjustments.slice().sort(function (a, b) {
      return String((b.orderEdit && b.orderEdit.committedAt) || b.createdAt || '').localeCompare(String((a.orderEdit && a.orderEdit.committedAt) || a.createdAt || ''));
    });
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — the FIRST pending collect in newest-first
       order is the live balance; every later one in the list is older and therefore superseded.
       findIndex (not a boolean) so a list with no pending collect marks nothing live, rather than
       defaulting to row 0 and pilling a figure that is already paid. */
    var liveIdx = -1;
    for (var i = 0; i < sorted.length; i++) {
      if (isPendingCollect(sorted[i])) { liveIdx = i; break; }
    }
    /* CUSTOM_ADJUSTUNDO_BY_CLAUDE (Ryan 2026-10-06, SHOP-2128) - Undo every adjustment where no money has moved.
       Ryan: on the row with the pill, above Adjust Order, same size / shape / colour / alignment. The worker decides
       whether it may show (`undo.available`, read after the paid reconcile); the button only opens the preview. */
    var undoIdx = -1;
    if (undo && undo.available && undo.targets && undo.targets.length) {
      var ids = undo.ids || [];
      if (liveIdx !== -1 && ids.indexOf(sorted[liveIdx].id) !== -1) undoIdx = liveIdx;
      else for (var j = 0; j < sorted.length; j++) { if (ids.indexOf(sorted[j].id) !== -1) { undoIdx = j; break; } }
      _undoTargets[el.getAttribute('data-ref') || ''] = undo.targets;
    }
    var undoBtn = '<button type="button" class="rqd-ao__card-btn rqd-ao__undo-btn" data-adjust-action="undo" data-ref="'
      + esc(el.getAttribute('data-ref') || '') + '" title="Put the order back the way it was before '
      + (undo && undo.count > 1 ? 'these ' + undo.count + ' adjustments' : 'this adjustment') + '. You see exactly what changes before anything happens.">Cancel</button>'; /* Ryan 2026-10-06: "Cancel", the width of Adjust order */
    var rows = sorted.map(function (a, i) { return histLine(a, i === liveIdx, i === undoIdx ? undoBtn : ''); }).join('');
    el.innerHTML = '<div class="rqd-ao__history-title">Adjustment history</div>' + heldRows + rows;
    el.classList.add('rqd-ao__history--has');
    if (heldRows) autoExpandCard(el);
    /* An unpaid balance is the one state where this card is not an "occasional corrective action"
       but the thing that needs doing, so it opens itself. Once per ref per session, mirroring
       sectionsAutoCollapsedRef — if Ryan then collapses it, it stays collapsed. */
    if (liveIdx !== -1) autoExpandCard(el);
  }

  var _autoExpanded = {};
  var _undoTargets = {}; // CUSTOM_ADJUSTUNDO_BY_CLAUDE - ref -> [{orderName, orderGid}] the card's Undo acts on
  function autoExpandCard(el) {
    try {
      var card = el.closest && el.closest('.rqd-detail__arap');
      if (!card) return;
      var ref = el.getAttribute('data-ref') || '';
      if (!ref || _autoExpanded[ref]) return;
      _autoExpanded[ref] = true;
      if (window.RQD && typeof window.RQD.expandSection === 'function') {
        window.RQD.expandSection(ref, card.dataset.section || 'adjust-order', card);
      }
    } catch (_) { /* cosmetic — never break the history render over it */ }
  }

  /* CUSTOM_RECONCILEDTOLD_BY_CLAUDE (AIOPS `fa96fefb5664`, Ryan 2026-09-18) — THE READ CAN CHANGE
     THE DEAL, SO THE BOARD IS REFETCHED WHEN IT DOES.
     There is no webhook for a customer paying a pay-link: `/order-adjust/state` IS the detector,
     and when it catches one it flips the settlement, stamps the Flow A balance gate and re-bakes
     the row server-side. The row ALREADY on screen was assembled before any of that, so SHOP-2022's
     Fulfillment card kept offering "Customer balance — will charge $179.31" with Invoice + Email
     and No Balance lit, twelve minutes after the money was in. Neither cache expiry helps: the
     board's SWR entry lives six hours in sessionStorage (so F5 repaints the same row) and the
     two-minute poll is skipped while any field has focus.
     ⚠️ `refreshDealNow` is quiet + force — past the 180s server cache, and it keeps the expanded
     deal open, which a plain refresh would collapse out from under whoever is standing in it.
     ⚠️ IT CANNOT LOOP. The repaint re-renders this card, which asks again — and the second
     reconcile finds the settlement already paid, so `dealChanged` is false and nothing refetches.
     The flag is only so the panel opening on top of a card that just asked does not fire twice. */
  var _dealRefreshInFlight = false;
  function noteDealChanged(st) {
    if (!st || !st.dealChanged || _dealRefreshInFlight) return;
    if (!(window.RQD && typeof window.RQD.refreshDealNow === 'function')) return;
    _dealRefreshInFlight = true;
    var done = function () { _dealRefreshInFlight = false; };
    try { Promise.resolve(window.RQD.refreshDealNow()).then(done, done); }
    catch (_) { done(); /* the history still rendered; the board is the lesser half */ }
  }

  function fillOne(el) {
    var ref = el.getAttribute('data-ref');
    if (!ref || el.dataset.aoLoaded === '1') return;
    el.dataset.aoLoaded = '1';
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — the on-card log covers the WHOLE deal.
       A family adjustment writes an entry into each order's own ledger, so reading only the
       parent's would show half the history of one change of mind — the same subset problem the
       panel itself had. Falls back to the single-order route when there is no cached order GID
       to resolve a family from. */
    var cc = _cache[ref];
    var p = (cc && cc.id)
      ? api().post('/order-adjust/family-state', { orderGid: cc.id, orderName: orderNameFor(ref), childRefs: cc.childRefs || [] })
        .then(function (st) {
          noteDealChanged(st);
          var map = (st && st.adjustments) || {};
          var list = Object.keys(map).reduce(function (acc, k) { return acc.concat(map[k] || []); }, [])
            .sort(function (a, b) { return String(a.createdAt || '').localeCompare(String(b.createdAt || '')); });
          var hm = (st && st.held) || {};
          var he = (st && st.heldEmail) || {};
          /* CUSTOM_ADJUSTUNDO_BY_CLAUDE - every order in the deal with something to undo. */
          var um = (st && st.undo) || {};
          var targets = ((st && st.orders) || []).filter(function (o) { return o && !o.unavailable && um[o.orderName] && um[o.orderName].available; })
            .map(function (o) { return { orderName: o.orderName, orderGid: o.orderGid }; });
          var undoIds = targets.reduce(function (acc, t) { return acc.concat(um[t.orderName].ids || []); }, []);
          return {
            list: list,
            held: Object.keys(hm).map(function (k) { return hm[k]; }).filter(Boolean),
            heldEmail: Object.keys(he).map(function (k) { return he[k]; }).filter(Boolean),
            undo: { available: targets.length > 0, count: undoIds.length, ids: undoIds, targets: targets },
          };
        })
      : api().post('/order-adjust/state', { orderName: orderNameFor(ref) })
        .then(function (st) { noteDealChanged(st); return { list: (st && st.adjustments) || [], held: (st && st.held) ? [st.held] : [], heldEmail: (st && st.heldEmail) ? [st.heldEmail] : [], undo: null /* CUSTOM_ADJUSTUNDO_BY_CLAUDE - no order id here to act on */ }; });
    p.then(function (res) {
      var list = res.list;
      renderHistoryInto(el, list, res.held, res.heldEmail, res.undo);
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — the Fulfillment card's PO row needs to
         know whether money settled AFTER the PO went out ("Re-send amended PO"). This fetch is the
         only place on the panel that holds the settlements, so it hands them over rather than
         making that card ask for the same thing again. ⚠️ Scoped to the detail panel this history
         belongs to, so one deal's settlements can never mark another deal's row. */
      try {
        var panel = el.closest ? el.closest('.rqd-detail') : null;
        if (window.RQD && typeof window.RQD.markPoResendNeeded === 'function') {
          window.RQD.markPoResendNeeded(panel || document, list);
        }
      } catch (_) { /* the history rendered; the badge is the lesser half */ }
    })
      .catch(function () { el.dataset.aoLoaded = ''; /* allow retry next render */ });
  }

  function fillHistories(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.rqd-ao__history[data-ref]').forEach(fillOne);
  }

  // Re-pull the on-card history for a ref after an adjustment (bypasses the
  // per-node cache so the new entry shows without a full DD reload).
  function refreshCardHistory(ref) {
    document.querySelectorAll('.rqd-ao__history[data-ref="' + (window.CSS && CSS.escape ? CSS.escape(ref) : ref) + '"]').forEach(function (el) {
      el.dataset.aoLoaded = '';
      fillOne(el);
    });
  }

  /* ── Modal shell ───────────────────────────────────────────────────────── */
  function closeModal() {
    var m = document.querySelector('.rqd-ao-modal-backdrop');
    if (m) m.remove();
    document.removeEventListener('keydown', _escClose);
  }
  function _escClose(e) { if (e.key === 'Escape') closeModal(); }

  function openModalShell(title) {
    closeModal();
    var back = document.createElement('div');
    // Mounts on <body>, outside the .rep-quote-dark wrap, so mirror the dark
    // state explicitly (same approach as the online-action modal).
    var isDark = !!document.querySelector('.rep-quote-portal-wrap.rep-quote-dark');
    back.className = 'rqd-ao-modal-backdrop' + (isDark ? ' rqd-ao-modal-backdrop--dark' : '');
    back.innerHTML =
      '<div class="rqd-ao-modal" role="dialog" aria-modal="true">'
      + '<button type="button" class="rqd-ao-modal__x" aria-label="Close">&times;</button>'
      + '<div class="rqd-ao-modal__title">' + esc(title) + '</div>'
      + '<div class="rqd-ao-modal__body"></div>'
      + '</div>';
    document.body.appendChild(back);
    // The modal is mounted on <body>, OUTSIDE the dashboard table whose click
    // handler owns data-adjust-action — so delegate those clicks here, or the
    // Email / Release buttons inside the modal do nothing.
    back.addEventListener('click', function (e) {
      var aoBtn = e.target.closest('[data-adjust-action]');
      if (aoBtn && !aoBtn.disabled) { if (window.RQD.adjustOrder) window.RQD.adjustOrder.handle(aoBtn); return; }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — both ends of the gesture must be on the backdrop; a drag out of the dialog (selecting a quantity) must not close it. See custom-rep-portal.js. */
      if (window.RepPortal.dismissedOnBackdrop(e, back)) closeModal();
    });
    back.querySelector('.rqd-ao-modal__x').addEventListener('click', closeModal);
    document.addEventListener('keydown', _escClose);
    return back.querySelector('.rqd-ao-modal__body');
  }

  /* ── Open panel ────────────────────────────────────────────────────────── */
  /* CUSTOM_REFUNDDOOR_BY_CLAUDE (AIOPS `281f35395eda`, Ryan 2026-09-14) — ⛔ THE PANEL NAMES
     THE DOOR IT WAS ENTERED THROUGH.
     ──────────────────────────────────────────────────────────────────────────────────────
     Ryan: cog — **Cancel/Refund** — **Refund part of it** — and a modal opens headed
     "Adjust Order". He filed it as a wiring bug: the refund option appears to open the wrong
     panel.

     ⛔ IT IS NOT THE WRONG PANEL, AND ROUTING IT ELSEWHERE WOULD BE THE REAL BUG. The 2026-07-29
     note on the chooser in dashboard-main.js settled this deliberately: *"the refund arm
     deliberately routes into the Adjust Order panel instead of growing a second money path
     here — that panel owns the refund ledger, the release step, Abby's email, and the
     revenue/GP/commission basis. Two doors, one engine."* A second partial-refund flow would be
     a second thing that moves money, and the two would drift.

     ⚠️ SO THE FIX IS THE DOOR, NOT THE ENGINE. What was actually broken is that the destination
     never acknowledged the choice: same title, same scroll position, refund controls somewhere
     below the add-items search. Now the chooser hands over the title it was chosen under and
     the panel opens ON the refund block. Nothing about the ledger, the rails or the money moves.

     ⚠️ OPTIONAL, SO EVERY OTHER CALLER IS UNTOUCHED — the detail card's own "Adjust Order"
     button, the post-commit reopen and the post-release reopen all pass no `opts` and still get
     "Adjust Order — {ref}" at the top, exactly as before. */
  function openPanel(ref, opts) {
    var c = _cache[ref];
    if (!c) { alert('Order data not loaded — refresh the dashboard and try again.'); return; }
    var body = openModalShell((opts && opts.title) || ('Adjust Order — ' + ref));
    body.innerHTML = '<div class="rqd-ao__loading">Loading…</div>';

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — /family-state, not /state.
       A deal can be several Shopify orders (SHOP-1933 + its balance child SHOP-1956), and the
       single-order call could only ever describe the one it was given — which is why this panel
       opened on 1 of that deal's 5 lines and said nothing about the other 4. The family route
       returns the parent AND its verified children; a deal that IS one order comes back as a
       family of one and everything below behaves exactly as it did before. */
    api().post('/order-adjust/family-state', {
      orderGid: c.id,
      orderName: orderNameFor(ref),
      childRefs: c.childRefs || [],
    })
      .then(function (st) {
        noteDealChanged(st);   // CUSTOM_RECONCILEDTOLD_BY_CLAUDE — opening the panel is a reconcile too
        var orders = (st && Array.isArray(st.orders) && st.orders.length) ? st.orders : null;
        if (!orders) throw new Error('no orders returned');
        var view = {
          id: c.id, reference: c.reference || ref,
          orderName: c.orderName, dealRef: c.dealRef,
          customerEmail: c.customerEmail, customer: c.customer,
          childRefs: c.childRefs || [],
          orders: orders,
          /* Kept for the handful of places that still think in one order: the parent's own
             numbers. Everything family-aware reads `orders`. */
          lineItems: (orders[0] && orders[0].lineItems) || c.lineItems,
          shipping: (orders[0] && orders[0].shipping) || c.shipping,
          fulfilled: !!(orders[0] && orders[0].fulfilled),
          /* CUSTOM_COLLECTTRACK_BY_CLAUDE (AIOPS 18e79f46cdac, Ryan 2026-09-04) — per order: how
             many pay-link emails this balance has had, when the last went, whether a follow-up is
             due. Read by the pending-collect row below. */
          lastCollectEmail: (st && st.lastCollectEmail) || {},
        };
        renderPanelBody(body, view, (st && st.adjustments) || {});
        _landOn(body, opts);
      })
      .catch(function () {
        /* Best-effort, exactly as before: fall back to the dashboard's cached copy so the admin
           can still build an adjustment. Shaped as a family of one so there is ONE render path. */
        renderPanelBody(body, {
          id: c.id, reference: c.reference || ref, orderName: c.orderName, dealRef: c.dealRef,
          customerEmail: c.customerEmail, customer: c.customer, childRefs: c.childRefs || [],
          lineItems: c.lineItems, shipping: c.shipping, fulfilled: c.fulfilled,
          orders: [{
            orderName: orderNameFor(ref), orderGid: c.id, isParent: true,
            lineItems: c.lineItems || [], shipping: c.shipping || 0, fulfilled: !!c.fulfilled,
          }],
        }, {});
        _landOn(body, opts);
      });
  }

  /* ⚠️ BEST-EFFORT, AND SILENT WHEN THERE IS NOTHING TO LAND ON. An order with no refund block
     (nothing refundable on it) simply opens at the top, which is where it opened before — so a
     missing block costs the old behaviour, never an error in front of an admin mid-refund. */
  function _landOn(body, opts) {
    /* CUSTOM_ROLLMATH_BY_CLAUDE (ONYX 4d660f6996ea, Ryan 2026-09-30) — "Adjust to 805" from the roll
       sub-row: set that line's New box and fire the box's own input listener, so the preview runs
       exactly as if the admin had typed it. The order changes only when the admin presses the panel's own button. A line the panel does not show (already
       removed, a different order) leaves the panel as it opened. */
    if (opts && opts.prefill && body) {
      var pf = opts.prefill;
      var box = body.querySelector('.rqd-ao__qty[data-line="' + _cssEsc(String(pf.lineItemId || '')) + '"]');
      if (box && pf.toQty > 0) {
        box.value = String(pf.toQty);
        try { box.dispatchEvent(new Event('input', { bubbles: true })); } catch (_ePf) {}
        box.classList.add('rqd-ao__qty--prefilled');
        try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_ePf2) {}
        try { box.focus({ preventScroll: true }); } catch (_ePf3) {}
      }
      /* CUSTOM_ONLINEWILLCALL_BY_CLAUDE (ONYX 96dc64a31604, Ryan 2026-10-26) - "Refund freight" on a will-call order: the
         freight box set to the cost kept ($0 = all of it) with no markup, and the preview run, so the panel shows the
         shipping refund. Nothing moves until the admin presses the panel's own buttons. */
      if (pf.freightCost != null) {
        var _fr = body.querySelector('.rqd-ao__freight[data-order="' + _cssEsc(String(pf.orderName || '')) + '"]') || body.querySelector('.rqd-ao__freight');
        var _fc = _fr && _fr.querySelector('.rqd-ao__fcost');
        var _fm = _fr && _fr.querySelector('.rqd-ao__fmarkup');
        if (_fm) { _fm.value = '0'; }
        if (_fc) {
          _fc.value = (Number(pf.freightCost) || 0).toFixed(2);
          try { _fc.dispatchEvent(new Event('input', { bubbles: true })); } catch (_eFr) {}
          _fc.classList.add('rqd-ao__qty--prefilled');
          try { _fc.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_eFr2) {}
          try { _fc.focus({ preventScroll: true }); } catch (_eFr3) {}
        }
      }
      return;
    }
    if (!opts || opts.focus !== 'refund' || !body) return;
    var blk = body.querySelector('.rqd-ao__refund[data-order]');
    if (!blk) return;
    try { blk.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
    catch (_e) { try { blk.scrollIntoView(); } catch (_e2) {} }
    var sel = blk.querySelector('.rqd-ao__rmode');
    if (sel) { try { sel.focus({ preventScroll: true }); } catch (_e3) {} }
  }

  /* Every order in the deal, always an array. The panel never branches on "is this a family" —
     a lone order is a family of one, which keeps one code path instead of two that drift. */
  function ordersOf(c) {
    return (c && Array.isArray(c.orders) && c.orders.length)
      ? c.orders
      : [{ orderName: (c && c.reference) || '', orderGid: (c && c.id) || '', isParent: true,
           lineItems: (c && c.lineItems) || [], shipping: (c && c.shipping) || 0, fulfilled: !!(c && c.fulfilled) }];
  }
  function isFamily(c) { return ordersOf(c).length > 1; }

  function renderPanelBody(body, c, adjustments) {
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — `adjustments` is now a MAP keyed by
       order name (the family route returns one ledger per member). Settlements are read PER
       ORDER and never pooled: each order has its OWN pay-link and its own refundable
       transactions, so a row that merged two orders' figures would point a button at money it
       cannot move. The collapse-many-collects-into-one rule below is per order for exactly the
       same reason — it was always about "the customer has ONE balance on ONE pay-link", and
       that sentence is scoped to an order. */
    var _adjMap = (adjustments && !Array.isArray(adjustments) && typeof adjustments === 'object')
      ? adjustments
      /* A pre-family caller (or the offline fallback) still hands over a flat array. Treat it as
         the parent's ledger so this function has one input shape. */
      : { __flat__: (Array.isArray(adjustments) ? adjustments : []) };

    var _orders = ordersOf(c);
    var _showOrderNames = isFamily(c);
    var h = '';

    _orders.forEach(function (o) {
      var list = _adjMap[o.orderName] || _adjMap.__flat__ || [];
      if (!list.length) return;
      h += renderSettlementsFor(c, o, list, _showOrderNames);
    });

    h += renderNewAdjustmentForm(c, _orders, _showOrderNames);
    body.innerHTML = h;
    wireForm(body, c);
  }

  /* One order's outstanding settlements — pending refunds, its live collect, a cleared balance.
     Lifted out of renderPanelBody unchanged in substance; it now runs once per family member
     instead of once per panel. `label` prefixes each row with the order name, but only when the
     deal actually spans more than one order — on the common single-order deal the rows read
     exactly as they always have. */
  /* CUSTOM_COLLECTTRACK_BY_CLAUDE (AIOPS suggestion 18e79f46cdac, Ryan 2026-09-04) — the chase
     state beside "pay-link emailed": how many asks this balance has had, how long since the last,
     and the verdict — awaiting payment, follow-up due, or time to call. Read off
     /order-adjust/family-state's `lastCollectEmail`, which the worker derives from the order's
     own pay-link email history; nothing here counts or decides. Empty when the state route did
     not carry it (an older worker, the offline fallback), so the row reads exactly as before. */
  function collectChaseText(c, o) {
    var m = (c && c.lastCollectEmail) || {};
    var t = m[o && o.orderName] || m.__flat__ || null;
    if (!t || !t.count) return '';
    var days = (t.daysSinceLast != null) ? Math.round(t.daysSinceLast) : null;
    var s = (t.count > 1 ? ' ×' + t.count : '')
      + (days != null ? ' · ' + (days === 0 ? 'today' : days + 'd ago') : '');
    if (!t.unpaid) return s;
    /* CUSTOM_COLLECTDRIP_BY_CLAUDE (Ryan 2026-09-08) — when Abby's balance follow-ups are running,
       say so here instead of "follow-up due" (she IS the follow-up); details and Stop live on the
       Deal Feed's ⚡ block. Read off the same `lastCollectEmail` object (`drip`), worker-derived. */
    var d = t.drip || null;
    if (d && d.armed) {
      var nx = d.sendAfter ? (fmtDate(d.sendAfter) || d.sendAfter) : '';
      s += ' · <span class="rqd-ao__hist-pending" title="Abby’s automatic balance follow-ups are running on this deal. Details and Stop on the Deal Feed.">&#9889; '
        + (d.closing
            ? 'Abby’s follow-ups sent; orders@ and Ryan are told' + (nx ? ' ' + esc(nx) : '') + ' if still unpaid'
            : 'Abby follow-up ' + esc(String(d.rung || 1)) + ' of ' + esc(String(d.of || 3)) + (nx ? ' goes ' + esc(nx) : '') + ' unless they pay')
        + '</span>';
      return s;
    }
    if (t.callSuggested) s += ' · <span class="rqd-ao__hist-pending" title="Two pay-link emails and still unpaid — time to call, not a third email.">time to call</span>';
    else if (t.followUpDue) s += ' · <span class="rqd-ao__hist-pending" title="Three business days since the last pay-link email and still unpaid.">follow-up due</span>';
    else s += ' · awaiting payment';
    return s;
  }

  function renderSettlementsFor(c, o, adjustments, label) {
    var tag = label ? '<span class="rqd-ao__ordertag">' + esc(o.orderName) + '</span> ' : '';
    var pending = adjustments.filter(function (a) {
      return a && a.settlement && a.settlement.type === 'refund' && a.settlement.status === 'pending';
    });
    var collects = adjustments.filter(function (a) {
      return a && a.settlement && a.settlement.type === 'invoice' && a.settlement.status === 'pending';
    });

    var h = '';

    // Pending refunds needing release (from a prior commit).
    if (pending.length) {
      h += '<div class="rqd-ao__pending">';
      pending.forEach(function (a) {
        h += '<div class="rqd-ao__pending-row" data-adj="' + esc(a.id) + '">'
          + '<div class="rqd-ao__pending-txt"><strong>' + tag + 'Refund pending release: ' + money(a.settlement.amount) + '</strong>'
          + '<span class="rqd-ao__muted"> — committed ' + esc((a.orderEdit && a.orderEdit.committedAt || '').slice(0, 10))
          + (a.reason ? ' · ' + esc(a.reason) : '') + '</span></div>'
          + '<div class="rqd-ao__pending-btns">'
          + '<button type="button" class="rqd-fb-btn rqd-fb-btn--primary" data-adjust-action="email-refund" data-ref="' + esc(c.reference) + '" data-order="' + esc(o.orderName) + '" data-adj="' + esc(a.id) + '" data-amt="' + esc(a.settlement.amount) + '">Email &amp; Refund ' + money(a.settlement.amount) + '</button>'
          + '<button type="button" class="rqd-ao__link-btn" data-adjust-action="release-refund" data-ref="' + esc(c.reference) + '" data-order="' + esc(o.orderName) + '" data-adj="' + esc(a.id) + '" data-amt="' + esc(a.settlement.amount) + '">Refund without emailing</button>'
          + '</div></div>';
      });
      h += '</div>';
    }

    // Pending collects — the order carries an outstanding balance; Abby emails the pay-link and
    // the customer pays online. No "release" button (money comes IN); the balance shows collected
    // on the next panel open once totalOutstanding hits 0.
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-14) — the customer has ONE balance on ONE
       pay-link, however many collects built it (SHOP-1941: goods, then freight once the supplier
       quoted dims). Show one row. The LAST pending collect carries the full outstanding — each
       commit re-reads the order's balance — so it owns the button; the email itemises all of
       them and the send stamps them all. Two rows would invite emailing the stale, lower one. */
    if (collects.length > 1) {
      var _newest = collects[collects.length - 1];
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the NEWEST figure, not the largest.
         Math.max encoded an assumption that a balance only ever grows, which was true while the
         only way to change one was another collect. A 'reduce' breaks it: the newest figure is the
         SMALLEST, and taking the max would have shown SHOP-1941's retired $2,100.93 above a button
         that emails $180.77. The comment directly above already said the last one carries the full
         outstanding, which is the correct rule in both directions. (Normally unreachable now —
         committing a reduce supersedes its predecessors, leaving one pending row — so this is the
         belt to that braces.) */
      var _amt = Number(_newest.settlement.amount) || 0;
      var _sentRec = collects.filter(function (a) { return a.email && a.email.sentAt; }).pop();
      var _payUrl = (_newest.settlement && _newest.settlement.payUrl)
        || (collects.filter(function (a) { return a.settlement && a.settlement.payUrl; })[0] || { settlement: {} }).settlement.payUrl;
      collects = [{
        id: _newest.id,
        reason: 'covers ' + collects.length + ' adjustments',
        email: _sentRec ? _sentRec.email : null,
        settlement: {
          type: 'invoice', status: 'pending', amount: _amt, payUrl: _payUrl || null,
          // Carry the direction through the collapse, or the merged row loses the reduce wording.
          direction: (_newest.settlement && _newest.settlement.direction) || undefined,
          previousAmount: (_newest.settlement && _newest.settlement.previousAmount) || undefined,
        },
      }];
    }
    if (collects.length) {
      h += '<div class="rqd-ao__pending">';
      collects.forEach(function (a) {
        var sent = a.email && a.email.sentAt;
        h += '<div class="rqd-ao__pending-row" data-adj="' + esc(a.id) + '">'
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — a reduced balance says so, and names
             what it replaced. "Balance to collect: $180.77" is true but hides the whole event; the
             admin needs to see at a glance that the figure came DOWN from $2,100.93, because that
             is what the customer is being emailed about. */
          + '<div class="rqd-ao__pending-txt"><strong>' + tag
          + (isReduced(a)
            ? 'Balance reduced to ' + money(a.settlement.amount)
              + (a.settlement.previousAmount ? ' (was ' + money(a.settlement.previousAmount) + ')' : '')
            : 'Balance to collect: ' + money(a.settlement.amount))
          + '</strong>'
          + '<span class="rqd-ao__muted"> — customer pays online'
          + (sent ? ' · pay-link emailed ' + esc(String(a.email.sentAt).slice(0, 10)) + collectChaseText(c, o) : '')
          + (a.reason ? ' · ' + esc(a.reason) : '') + '</span></div>'
          + '<div class="rqd-ao__pending-btns">'
          + '<button type="button" class="rqd-fb-btn rqd-fb-btn--primary" data-adjust-action="email-collect" data-ref="' + esc(c.reference) + '" data-order="' + esc(o.orderName) + '" data-adj="' + esc(a.id) + '" data-amt="' + esc(a.settlement.amount) + '">'
          + (isReduced(a) ? (sent ? 'Resend revised balance' : 'Email revised balance') : (sent ? 'Resend pay-link' : 'Email pay-link')) + '</button>'
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — "Open pay-link" → COPY. Opening the
             link in a tab was the wrong verb: the thing Ryan actually does with it is paste it into
             an email he's writing himself, and opening it meant a tab, a select-all in the address
             bar, then a copy. The URL still rides on data-url so nothing is lost if the clipboard
             write is refused. */
          + (a.settlement.payUrl ? '<button type="button" class="rqd-ao__link-btn" data-adjust-action="copy-paylink" data-url="' + esc(a.settlement.payUrl) + '" title="Copy the pay-link to your clipboard">Copy pay-link</button>' : '')
          + '</div></div>';
      });
      h += '</div>';
    }

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — a reduction that wiped the balance to the
       cent settles as type 'none', so it appears in neither list above and the customer would have
       had no way to be TOLD. ⛔ Every other settlement has an exit that ends in an email; this one
       hung off nothing. One row, one button, no money to move. */
    var cleared = adjustments.filter(function (a) {
      return isReduced(a) && a.settlement && a.settlement.type === 'none' && !(a.email && a.email.sentAt);
    });
    if (cleared.length) {
      var _cl = cleared[cleared.length - 1];
      h += '<div class="rqd-ao__pending"><div class="rqd-ao__pending-row" data-adj="' + esc(_cl.id) + '">'
        + '<div class="rqd-ao__pending-txt"><strong>Balance cleared</strong>'
        + '<span class="rqd-ao__muted"> — nothing further owed'
        + (_cl.settlement.previousAmount ? ' (was ' + money(_cl.settlement.previousAmount) + ')' : '')
        + ' · the customer has not been told yet</span></div>'
        + '<div class="rqd-ao__pending-btns">'
        + '<button type="button" class="rqd-fb-btn rqd-fb-btn--primary" data-adjust-action="email-collect" data-ref="' + esc(c.reference) + '" data-order="' + esc(o.orderName) + '" data-adj="' + esc(_cl.id) + '" data-amt="0">' + (label ? 'Email the customer (' + esc(o.orderName) + ')' : 'Email the customer') + '</button>'
        + '</div></div></div>';
    }

    /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — AN EVEN SWAP HAS TO HAVE AN EXIT TOO.
       Same shape, same reasoning and the same fix as the Balance-cleared row above it: a swap
       priced to match settles as type 'none', so it appears in neither pending list and the
       customer has no way to be told their colour changed. ⛔ AND THIS ONE IS WORSE THAN A
       CLEARED BALANCE: nothing about the order's money moved, so there is no second signal
       anywhere — no refund, no invoice, no Shopify notification (the commit runs
       notifyCustomer:false) — and the first they would hear of it is a box arriving in a
       colour they did not order last time they looked.

       ⛔ NOT FOLDED INTO THE `cleared` BLOCK: that one is gated on `isReduced`, says
       "Balance cleared", and names a figure that no longer stands. This says what actually
       happened. One row, one button, no money to move. */
    var evenSwaps = adjustments.filter(function (a) {
      if (!a || !a.settlement || a.settlement.type !== 'none') return false;
      if (a.email && a.email.sentAt) return false;
      if (isReduced(a)) return false;            // the Balance-cleared row owns that one
      return (a.additions || []).some(function (ad) { return ad && ad.swapOf; });
    });
    if (evenSwaps.length) {
      var _sw = evenSwaps[evenSwaps.length - 1];
      var _swWhat = (_sw.additions || []).filter(function (ad) { return ad && ad.swapOf; })
        .map(function (ad) {
          var out = (_sw.lines || []).filter(function (l) { return l.lineItemId === ad.swapOf; })[0];
          return (out ? out.title + ' → ' : '') + (ad.title || ad.sku || 'a replacement');
        }).join(', ');
      h += '<div class="rqd-ao__pending"><div class="rqd-ao__pending-row" data-adj="' + esc(_sw.id) + '">'
        + '<div class="rqd-ao__pending-txt"><strong>Item swapped</strong>'
        + '<span class="rqd-ao__muted"> — ' + esc(_swWhat)
        + ' · no money moved · the customer has not been told yet</span></div>'
        + '<div class="rqd-ao__pending-btns">'
        + '<button type="button" class="rqd-fb-btn rqd-fb-btn--primary" data-adjust-action="email-collect" data-ref="' + esc(c.reference) + '" data-order="' + esc(o.orderName) + '" data-adj="' + esc(_sw.id) + '" data-amt="0">'
        + (label ? 'Email the customer (' + esc(o.orderName) + ')' : 'Email the customer') + '</button>'
        + '</div></div></div>';
    }

    return h;
  }

  /* The new-adjustment form. Line rows are grouped under their OWN order, because on a family
     the same SKU can sit on two of them (SHOP-1933 and SHOP-1956 both carry
     LMNT-000507 Rally Roll) and an ungrouped list would make "which 224 am I changing?"
     unanswerable. Freight and Direct refund are per order for a harder reason than layout:
     each order has its own shipping line and its own sale transaction, so those controls
     cannot mean anything except against one named order. */
  function renderNewAdjustmentForm(c, orders, showOrderNames) {
    var h = '';

    if (showOrderNames) {
      h += '<p class="rqd-ao__famnote">This deal is <strong>' + orders.length + ' Shopify orders</strong> — '
        + orders.map(function (o) { return esc(o.orderName) + ' (' + ((o.lineItems || []).length) + ')'; }).join(' + ')
        + '. All of their lines are below. '
        + '<span class="rqd-ao__muted">Shopify settles money per order, so a new item is put on the order you are '
        + 'reducing wherever possible — that keeps it to one refund or one pay-link instead of both.</span></p>';
    }

    var anyFulfilled = orders.some(function (o) { return o.fulfilled; });
    if (anyFulfilled) {
      h += '<p class="rqd-ao__warn">⚠ '
        + (showOrderNames
          ? esc(orders.filter(function (o) { return o.fulfilled; }).map(function (o) { return o.orderName; }).join(', ')) + ' is fully fulfilled'
          : 'This order is fully fulfilled')
        + ' — Shopify won’t let us edit a shipped line, '
        + 'so quantity changes below will be refused. To hand money back, use <strong>Direct refund</strong>; '
        + 'to collect more, raise the freight or a quantity (additive edits are still allowed).</p>';
    }

    // New-adjustment form.
    h += '<div class="rqd-ao__form">';
    h += '<div class="rqd-ao__label">Line quantities</div>';

    orders.forEach(function (o) {
      if (o.unavailable) {
        /* ⛔ Reported, never hidden. A member we could not read is exactly the situation this
           whole feature exists to stop being invisible. */
        h += '<p class="rqd-ao__warn">⚠ ' + esc(o.orderName) + ' belongs to this deal but could not be loaded ('
          + esc(o.unavailable) + '). Its lines are NOT shown and will not be adjusted.</p>';
        return;
      }
      if (showOrderNames) {
        h += '<div class="rqd-ao__ordergroup">' + esc(o.orderName)
          + (o.isParent ? '<span class="rqd-ao__muted"> · original order</span>' : '<span class="rqd-ao__muted"> · added later</span>')
          + '</div>';
      }
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — A LEADING CONTROL COLUMN.
         The qty box answers "how many of this"; there was nowhere to answer "not this one,
         that one". So a colour change after the sale meant typing 0 here and then hunting the
         replacement up again in the Add box below — two unrelated acts, which the worker
         then refused to settle together whenever the new item cost more. */
      h += '<table class="rqd-ao__lines"><thead><tr><th class="rqd-ao__swapcol"></th><th>Item</th><th class="ta-r">Now</th><th class="ta-r">New</th></tr></thead><tbody>';
      (o.lineItems || []).forEach(function (li) {
        var q0 = Number(li.quantity) || 0;
        h += '<tr data-line-row="' + esc(li.lineItemId) + '">'
          + '<td class="rqd-ao__swapcol"><button type="button" class="rqd-ao__swapbtn"'
          + ' title="Swap this item for another — a different colour, or a different product"'
          + ' data-line="' + esc(li.lineItemId) + '"'
          + ' data-order="' + esc(o.orderName) + '"'
          + ' data-qty="' + q0 + '"'
          + ' data-handle="' + esc(li.productHandle || '') + '"'
          + ' data-variant="' + esc(li.variantId || '') + '"'
          + ' data-price="' + (Number(li.unitPrice) || 0) + '"'
          + ' data-label="' + esc(li.title || '') + '">⇄</button></td>'
          + '<td>' + esc(li.title || '') + (li.sku ? '<span class="rqd-ao__muted"> · ' + esc(li.sku) + '</span>' : '') + '</td>'
          + '<td class="ta-r">' + q0 + '</td>'
          + '<td class="ta-r"><input type="number" min="0" step="1" class="rqd-ao__qty" '
          + 'data-order="' + esc(o.orderName) + '" '
          + 'data-line="' + esc(li.lineItemId) + '" data-from="' + q0 + '" value="' + q0 + '" style="width:64px;"></td>'
          + '</tr>';
      });
      h += '</tbody></table>';
    });

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-10) — Add an item (orderEditAddVariant).
       The last open collect gap: qty↑ and freight↑ could already collect, but a NEW SKU had
       no path. Driving case is a Legend machine sold at full price whose buyer then adds a
       weight-stack upgrade — it rides the SAME adjustment as the freight true-up, so the
       customer gets one email and one pay-link instead of two.

       Only the variant id + quantity are sent; the worker resolves price/title/SKU from
       Shopify, because orderEditAddVariant charges the variant's own current price and a
       stale client price would disagree with it.

       CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — plus `noCharge`, the one add field the
       client really does decide: it is an intention, not a fact about the product. */
    h += '<div class="rqd-ao__label">Add an item <span class="rqd-ao__muted">— '
      + (showOrderNames ? 'puts a new SKU on one of the orders above' : 'puts a new SKU on this order')
      + ', or tick <strong>No charge</strong> to give it away</span></div>';
    h += '<div class="rqd-ao__add">'
      + '<input type="search" class="rqd-ao__addq" placeholder="Search by SKU or title — e.g. WS-UPGRADE" autocomplete="off" style="width:100%;box-sizing:border-box;">'
      + '<div class="rqd-ao__addresults" hidden></div>'
      + '<table class="rqd-ao__added" hidden><tbody></tbody></table>'
      + '</div>';

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — FREIGHT IS PER ORDER, and not for
       layout reasons: each order carries its own shipping line, so "the freight" is only ever
       meaningful against one named order. A single box over a family would have to pick one
       silently. */
    h += '<div class="rqd-ao__label">Freight</div>';
    orders.forEach(function (o) {
      if (o.unavailable) return;
      h += '<div class="rqd-ao__freight" data-order="' + esc(o.orderName) + '">'
        + (showOrderNames ? '<span class="rqd-ao__ordertag">' + esc(o.orderName) + '</span>' : '')
        /* CUSTOM_FREIGHTREMINDER_BY_CLAUDE (Ryan 2026-08-29) — pre-filled from the cost already on
           the deal, so the Fulfillment card and this box are two doors to one number.
           ⚠️ ONLY ON THE ORDER THAT OWNS THE FIGURE. `financials.freightCost` belongs to the deal
           whose panel this is; on a family, a child order's freight is its own and pre-filling it
           from the parent's would put the parent's cost on the child's shipping line. */
        + '<label>Cost <input type="number" min="0" step="0.01" class="rqd-ao__fcost" placeholder="0.00" style="width:96px;"'
        + ((o.orderName === c.reference && Number(c.freightCostSaved) > 0)
            ? ' value="' + esc(Number(c.freightCostSaved).toFixed(2)) + '"' : '')
        + '></label>'
        + '<label>Markup % <input type="number" min="0" step="1" class="rqd-ao__fmarkup" value="25" style="width:64px;"></label>'
        + '<span class="rqd-ao__muted">Currently charged: ' + money(o.shipping || 0) + '</span>'
        + '</div>';
    });

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-07-29) — Direct refund ('refund-only' rail).
       The one control that works on an already-DELIVERED order: it pays out against the
       order's sale transaction instead of editing the order, so Shopify's "can't touch a
       fulfilled line" rule never applies. Four ways to reach the number — they're pure
       calculators, all settling through the same refund. Mutually exclusive with the line/
       freight fields above (the worker refuses the mix rather than move the money twice). */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — ALSO per order, and for a stricter
       reason than freight: a direct refund pays out against that order's OWN sale transaction.
       Pointing one at a line belonging to a different order would ask Shopify to refund money
       a different capture is holding. The line dropdown is therefore scoped to its own order. */
    h += '<div class="rqd-ao__label">Direct refund <span class="rqd-ao__muted">— no order edit; works after delivery</span></div>';
    orders.forEach(function (o) {
      if (o.unavailable) return;
      h += '<div class="rqd-ao__refund" data-order="' + esc(o.orderName) + '">'
        + (showOrderNames ? '<span class="rqd-ao__ordertag">' + esc(o.orderName) + '</span>' : '')
        + '<select class="rqd-ao__rmode">'
        + '<option value="">— none —</option>'
        + '<option value="flat">Flat amount</option>'
        + '<option value="line">A whole line item</option>'
        + '<option value="line-percent">% of a line item</option>'
        + '<option value="order-percent">% of the order</option>'
        + '</select>'
        + '<label class="rqd-ao__rfield rqd-ao__rfield--amount" hidden>$ <input type="number" min="0" step="0.01" class="rqd-ao__ramount" placeholder="0.00" style="width:96px;"></label>'
        + '<label class="rqd-ao__rfield rqd-ao__rfield--percent" hidden><input type="number" min="0" max="100" step="0.1" class="rqd-ao__rpercent" placeholder="0" style="width:64px;"> %</label>'
        + '<select class="rqd-ao__rline rqd-ao__rfield rqd-ao__rfield--line" hidden>'
        + (o.lineItems || []).map(function (li) {
          return '<option value="' + esc(li.lineItemId) + '">' + esc(li.title || '') + ' (' + (Number(li.quantity) || 0) + ')</option>';
        }).join('')
        + '</select>'
        + '</div>';
    });

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-05) — relabelled: the reason is INTERNAL now.
       It no longer appears in the customer email, and no longer goes into the freight line-item
       title on the order. Both were removed because the box looks internal, so sooner or later
       someone types what they'd write in a note and the customer reads it. */
    h += '<div class="rqd-ao__label">Reason (internal only — not shown to the customer)</div>';
    h += '<textarea class="rqd-ao__reason" rows="2" maxlength="500" placeholder="Internal note: why this adjustment is being made (e.g. liftgate billed twice by the carrier)."></textarea>';

    h += '<div class="rqd-ao__preview"></div>';
    h += '<div class="rqd-ao__actions"><button type="button" class="rqd-fb-btn rqd-fb-btn--primary rqd-ao__commit" disabled>Commit adjustment</button></div>';
    h += '</div>';
    return h;
  }

  /* ── Live preview + commit wiring ──────────────────────────────────────── */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — reads the form as ONE SLICE PER ORDER.
     Every control now carries the order it belongs to (`data-order` on the qty inputs, on each
     freight block, on each refund block), so nothing has to be inferred from position. The
     returned shape is what /order-adjust/family-preview and /family-commit take.

     `addVariants` entries deliberately go out WITHOUT an orderName unless the admin picked one:
     that absence is what asks the worker to steer the item onto the order being reduced. */
  function readForm(body, c) {
    var orders = ordersOf(c);
    var byOrder = {};
    orders.forEach(function (o) {
      byOrder[o.orderName] = { orderName: o.orderName, lineChanges: [], freight: null, refund: null };
    });
    function slot(name) {
      if (!byOrder[name]) byOrder[name] = { orderName: name, lineChanges: [], freight: null, refund: null };
      return byOrder[name];
    }

    body.querySelectorAll('.rqd-ao__qty').forEach(function (inp) {
      var to = parseInt(inp.value, 10);
      var from = parseInt(inp.getAttribute('data-from'), 10);
      if (isNaN(to) || to === from) return;
      slot(inp.getAttribute('data-order') || orders[0].orderName)
        .lineChanges.push({ lineItemId: inp.getAttribute('data-line'), toQty: to });
    });

    body.querySelectorAll('.rqd-ao__freight[data-order]').forEach(function (blk) {
      var fcost = parseFloat(blk.querySelector('.rqd-ao__fcost').value);
      if (isNaN(fcost) || fcost < 0) return;
      var fmarkup = parseFloat(blk.querySelector('.rqd-ao__fmarkup').value);
      slot(blk.getAttribute('data-order')).freight = { cost: fcost, markupPercent: isNaN(fmarkup) ? 0 : fmarkup };
    });

    /* Direct-refund request. Only the fields the chosen mode actually uses are sent, so a
       stale value left in a hidden input can't leak into the payload. */
    body.querySelectorAll('.rqd-ao__refund[data-order]').forEach(function (blk) {
      var rmodeEl = blk.querySelector('.rqd-ao__rmode');
      var rmode = rmodeEl ? rmodeEl.value : '';
      if (!rmode) return;
      var refund = { mode: rmode };
      if (rmode === 'flat') {
        var ramt = parseFloat(blk.querySelector('.rqd-ao__ramount').value);
        if (isNaN(ramt)) return;
        refund.amount = ramt;
      } else {
        if (rmode === 'line-percent' || rmode === 'order-percent') {
          var rpct = parseFloat(blk.querySelector('.rqd-ao__rpercent').value);
          if (isNaN(rpct)) return;
          refund.percent = rpct;
        }
        if (rmode === 'line' || rmode === 'line-percent') {
          refund.lineItemId = blk.querySelector('.rqd-ao__rline').value || '';
        }
      }
      slot(blk.getAttribute('data-order')).refund = refund;
    });

    /* Added SKUs. Only id + quantity (+ an optional chosen order) travel — the worker resolves
       price/title/SKU from Shopify, because orderEditAddVariant charges the variant's own
       current price and a stale client price would disagree with it. A row whose quantity has
       been blanked or zeroed is simply not sent, so clearing the box removes it. */
    var addVariants = [];
    body.querySelectorAll('.rqd-ao__added tr[data-variant]').forEach(function (tr) {
      var qEl = tr.querySelector('.rqd-ao__addqty');
      var q = parseInt(qEl ? qEl.value : '', 10);
      if (isNaN(q) || q <= 0) return;
      var pick = tr.querySelector('.rqd-ao__addorder');
      var chosen = pick ? pick.value : '';
      var row = { variantId: tr.getAttribute('data-variant'), quantity: q, title: tr.getAttribute('data-title') || '' };
      /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — the one add field that is the admin's
         decision rather than Shopify's fact, so unlike price/title/sku it genuinely does travel
         from the client. Sent only when ticked, so an untouched row is byte-for-byte the payload
         this route has always taken. */
      var _free = tr.querySelector('.rqd-ao__addfree');
      if (_free && _free.checked) row.noCharge = true;
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — THE PAIRING, and it is the only
         thing that tells an exchange apart from a removal that happens to sit beside an
         addition. The worker refuses a net-collect alongside a line going down (you cannot
         collect for a line that went down without double-charging) and lifts that refusal
         ONLY for a decrease a `swapOf` names, where the two legs are the same goods.

         ⛔ THE DOWN-LEG NEEDS NOTHING SPECIAL HERE: the Swap control set that row's qty
         box to 0, so the ordinary qty reader above has already emitted it. One mechanism. */
      var _swapOf = tr.getAttribute('data-swap-of');
      if (_swapOf) row.swapOf = _swapOf;
      /* The price the admin chose for the incoming line, when they changed it. Absent means
         "charge what Shopify charges", which is what every add has always done. */
      var _priceEl = tr.querySelector('.rqd-ao__addprice');
      if (_priceEl && _priceEl.value !== '') {
        var _p = parseFloat(_priceEl.value);
        if (!isNaN(_p) && _p >= 0) row.targetUnitPrice = _p;
      }
      if (chosen) row.orderName = chosen;   // absent = let the worker steer it
      addVariants.push(row);
    });

    var slices = orders.map(function (o) { return byOrder[o.orderName]; }).filter(Boolean);
    return {
      orders: slices,
      addVariants: addVariants,
      hasAny: slices.some(function (s) {
        return s.lineChanges.length || s.freight || s.refund;
      }) || addVariants.length > 0,
    };
  }

  var _debounce;
  function wireForm(body, c) {
    var previewEl = body.querySelector('.rqd-ao__preview');
    var commitBtn = body.querySelector('.rqd-ao__commit');
    var _lastAdj = null;

    function refresh() {
      var input = readForm(body, c);
      if (!input.hasAny) {
        previewEl.innerHTML = '';
        commitBtn.disabled = true;
        _lastAdj = null;
        return;
      }
      previewEl.innerHTML = '<div class="rqd-ao__muted">Calculating…</div>';
      api().post('/order-adjust/family-preview', {
        orderGid: c.id,
        ref: (c && c.dealRef) || null,
        childRefs: c.childRefs || [],
        orders: input.orders,
        addVariants: input.addVariants,
      })
        .then(function (r) {
          if (!r || !r.rollup) throw new Error((r && r.error) || 'Preview failed');
          _lastAdj = r;
          renderFamilyPreview(previewEl, commitBtn, r, c);
        })
        .catch(function (e) {
          previewEl.innerHTML = '<div class="rqd-ao__err">' + esc(e.message || 'Preview failed') + '</div>';
          commitBtn.disabled = true;
          _lastAdj = null;
        });
    }

    body.querySelectorAll('.rqd-ao__qty, .rqd-ao__fcost, .rqd-ao__fmarkup, .rqd-ao__ramount, .rqd-ao__rpercent').forEach(function (el) {
      el.addEventListener('input', function () { clearTimeout(_debounce); _debounce = setTimeout(refresh, 350); });
    });

    /* ── Add-an-item search ──────────────────────────────────────────────────
       /product-search is the Quote Workstation's existing endpoint — it already returns
       variants with id/sku/price, so the picker needs no new backend route. It is a fuzzy
       word search, so a title term like "weight stack upgrade" pulls in every machine with
       those words; searching the SKU is the precise way in. Hence the SKU-first placeholder. */
    var addQ = body.querySelector('.rqd-ao__addq');
    var addResults = body.querySelector('.rqd-ao__addresults');
    var addedTbl = body.querySelector('.rqd-ao__added');
    var _addDebounce;

    function syncAddedVisibility() {
      addedTbl.hidden = !addedTbl.querySelector('tr[data-variant]');
    }

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — on a family, each added row gets an
       order picker that defaults to "Auto". Auto is not a cop-out: it sends NO order name, which
       is what lets the worker put the item on whichever order is being reduced, so the family
       settles as one movement instead of a refund on one order and a bill on another. The
       explicit options exist for when the admin knows better; the preview then reports where each
       item actually landed, so the choice is never invisible. */
    var _famOrders = ordersOf(c).filter(function (o) { return !o.unavailable; });
    var _famPick = _famOrders.length > 1;

    /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — `swap` turns an addition into an EXCHANGE:
       the row remembers the line it replaces, is laid out like a Quote Workstation row, and spans
       the whole table. An ordinary add is untouched and renders exactly as it always has. */
    function addRow(v, swap) {
      if (addedTbl.querySelector('tr[data-variant="' + _cssEsc(v.id) + '"]')) return;
      var tr = document.createElement('tr');
      tr.setAttribute('data-variant', v.id);
      tr.setAttribute('data-title', v.label || '');
      if (swap) tr.setAttribute('data-swap-of', swap.lineItemId);

      if (swap) {
        /* ── THE SWAP ROW ────────────────────────────────────────────────────
           Ryan: *"add the MSRP, MAP, and points.. sort of lay it out like we were on the QW, so I
           can cleanly make adjustments to the points to adjust what we're charging them... and we
           need it to be ultra clear what the points/MAP/MSRP/cost/amount charged was for the
           product we're replacing."*

           ⛔ POINTS IS THE INPUT AND CHARGE IS DERIVED — the QW's own direction, and the reason
           the layout was asked for: you price by margin, not by typing a number and working out
           afterwards what it earned. `calculateDirect` (cost / (1 − pts/100)) is the house formula
           in assets/workstation-core.js; it is reproduced in `_derive` below because that file is
           not loaded on the Deal Dashboard. ⚠️ Keep the two in step.

           ⛔ IT SPANS THE TABLE. Squeezed into the first cell of the add table the outgoing
           product's name wrapped mid-title (Ryan: *"it wraps at the word 'Flooring:'"*) and there
           was nowhere to put seven figures. A swap is one decision about one line, so it gets the
           width of one line.

           ⚠️ MAP IS A CEILING HERE, NOT A GUIDELINE. The QW red-outlines a direct price above MAP
           as a warning; Shopify will REFUSE to bill it, because `orderEditAddVariant` takes no
           price and the only lever is a discount off the variant's own. Same red, harder meaning,
           and the note says so rather than letting Commit fail with a surprise.

           ⚠️ NO COST, NO POINTS. A variant Shopify holds no unit cost for cannot derive a price
           from a margin, so the row falls back to the plain Charge box and says why — a points
           field that silently means nothing is worse than no points field at all. */
        var _cost = Number(v.cost) || 0;
        var _map = Number(v.price) || 0;          // the website price: MAP, and the hard ceiling
        var _msrp = Number(v.msrp) || 0;
        var _q0 = Number(swap.qty) || 1;
        var _oq = swap.quote || {};
        var _outUnit = Number(swap.unitPrice || _oq.directPrice || 0) || 0;
        /* Default to the margin the line being replaced was sold at: holding the deal's own points
           is the common case, and it makes it zero typing. Failing that, the incoming variant's
           own margin off its website price. */
        var _pts0 = (_oq.points != null) ? Number(_oq.points)
          : ((_cost > 0 && _map > 0) ? Math.round((1 - _cost / _map) * 1000) / 10 : null);
        var _canPts = _cost > 0 && _pts0 != null;
        var DASH = '—';

        function fig(lbl, val, cls) {
          return '<span class="rqd-ao__fig' + (cls ? ' ' + cls : '') + '"><i>' + lbl + '</i>' + val + '</span>';
        }
        var outFigs = fig('Qty', String(_q0))
          + (_oq.msrp ? fig('MSRP', money(_oq.msrp)) : '')
          + (_oq.mapPrice ? fig('MAP', money(_oq.mapPrice)) : '')
          + (_oq.cost ? fig('Cost', money(_oq.cost)) : '')
          + (_oq.points != null ? fig('Pts', Number(_oq.points).toFixed(1)) : '')
          + (_outUnit ? fig('Charged', money(_outUnit) + ' ea') : '')
          + (_outUnit ? fig('Total', money(_outUnit * _q0)) : '');

        tr.innerHTML = '<td colspan="6"><div class="rqd-ao__swaprow">'
          + '<div class="rqd-ao__swapside rqd-ao__swapside--out">'
          + '<div class="rqd-ao__swapside-hd"><span class="rqd-ao__swaptag">Replacing</span>'
          + '<span class="rqd-ao__swapname">' + esc(swap.label) + '</span></div>'
          + (_oq.sku ? '<div class="rqd-ao__swapsku">' + esc(_oq.sku) + '</div>' : '')
          + '<div class="rqd-ao__figs">'
          + (outFigs || '<span class="rqd-ao__muted">No quote figures recorded for this line.</span>')
          + '</div></div>'
          + '<div class="rqd-ao__swapside rqd-ao__swapside--in">'
          + '<div class="rqd-ao__swapside-hd"><span class="rqd-ao__swaptag rqd-ao__swaptag--in">With</span>'
          + '<span class="rqd-ao__swapname">' + esc(v.label) + '</span></div>'
          + (v.sku ? '<div class="rqd-ao__swapsku">' + esc(v.sku) + '</div>' : '')
          + '<div class="rqd-ao__figs">'
          + '<label class="rqd-ao__fig"><i>Qty</i><input type="number" min="1" step="1" class="rqd-ao__addqty" value="' + _q0 + '"></label>'
          + (_msrp ? fig('MSRP', money(_msrp)) : '')
          + (_map ? fig('MAP', money(_map)) : '')
          + (_cost ? fig('Cost', money(_cost)) : '')
          + (_canPts
            ? '<label class="rqd-ao__fig"><i>Pts</i><input type="number" step="0.1" class="rqd-ao__addpts" value="' + _pts0.toFixed(1) + '"></label>'
            : '<label class="rqd-ao__fig"><i>Charge</i><input type="number" min="0" step="0.01" class="rqd-ao__addprice" value="' + _map.toFixed(2) + '" data-max="' + _map + '"></label>')
          + (_canPts ? fig('Charge', '<b class="rqd-ao__chargeout">' + DASH + '</b>', 'rqd-ao__fig--derived') : '')
          + fig('Total', '<b class="rqd-ao__totalout">' + DASH + '</b>', 'rqd-ao__fig--derived')
          + fig('Change', '<b class="rqd-ao__deltaout">' + DASH + '</b>', 'rqd-ao__fig--derived')
          + (_canPts ? '<input type="hidden" class="rqd-ao__addprice" data-max="' + _map + '">' : '')
          + '</div>'
          + '<div class="rqd-ao__pricenote"></div>'
          + '</div>'
          + '<div class="rqd-ao__swapactions">'
          + (_famPick
            ? '<select class="rqd-ao__addorder" title="Which order this item goes on. Auto puts it on the order being reduced, so the change settles as one movement."><option value="">Auto</option>'
              + _famOrders.map(function (o) { return '<option value="' + esc(o.orderName) + '">' + esc(o.orderName) + '</option>'; }).join('')
              + '</select>'
            : '')
          + '<label class="rqd-ao__nocharge" title="Put this item on the order and give it away. The order total does not change and the customer is not emailed."><input type="checkbox" class="rqd-ao__addfree"> No charge</label>'
          + '<button type="button" class="rqd-ao__link-btn rqd-ao__addrm">Remove</button>'
          + '</div></div></td>';

        addedTbl.querySelector('tbody').appendChild(tr);

        var ptsEl = tr.querySelector('.rqd-ao__addpts');
        var priceEl = tr.querySelector('.rqd-ao__addprice');
        var qtyEl = tr.querySelector('.rqd-ao__addqty');
        var freeEl = tr.querySelector('.rqd-ao__addfree');
        var noteEl = tr.querySelector('.rqd-ao__pricenote');
        var chargeOut = tr.querySelector('.rqd-ao__chargeout');
        var totalOut = tr.querySelector('.rqd-ao__totalout');
        var deltaOut = tr.querySelector('.rqd-ao__deltaout');

        /* The whole derivation, re-run on every keystroke. It writes the hidden `.rqd-ao__addprice`
           that readForm sends, so the wire contract is unchanged: the panel still sends a target
           unit price and the worker still decides whether it is legal. */
        function _derive() {
          var qty = parseInt(qtyEl.value, 10);
          if (!(qty > 0)) qty = 0;
          var unit;
          if (_canPts) {
            var pts = parseFloat(ptsEl.value);
            if (isNaN(pts)) pts = 0;
            /* calculateDirect, from workstation-core.js: 100 points or more has no finite price,
               and 0 (or blank) means sell at cost. */
            unit = (pts >= 100) ? _cost : (pts ? Math.round(_cost / (1 - pts / 100) * 100) / 100 : _cost);
            priceEl.value = unit.toFixed(2);
          } else {
            unit = parseFloat(priceEl.value);
            if (isNaN(unit) || unit < 0) unit = 0;
          }
          var free = freeEl && freeEl.checked;
          var line = free ? 0 : Math.round(unit * qty * 100) / 100;
          var was = Math.round(_outUnit * _q0 * 100) / 100;
          var delta = Math.round((line - was) * 100) / 100;

          if (chargeOut) chargeOut.textContent = free ? 'FREE' : money(unit);
          if (totalOut) totalOut.textContent = free ? money(0) : money(line);
          if (deltaOut) {
            deltaOut.textContent = Math.abs(delta) < 0.005 ? 'even'
              : ((delta < 0 ? '−' : '+') + money(Math.abs(delta)));
            deltaOut.className = 'rqd-ao__deltaout '
              + (Math.abs(delta) < 0.005 ? '' : (delta < 0 ? 'rqd-ao__credit' : 'rqd-ao__charge'));
          }

          /* ⚠️ OVER MAP IS A REFUSAL, NOT A STYLE. Say it here, with the number, rather than
             letting the preview come back with a server error the admin has to decode. */
          var over = !free && _map > 0 && unit > _map + 0.005;
          tr.classList.toggle('rqd-ao__swaprow--overmap', over);
          if (noteEl) {
            noteEl.textContent = over
              ? ('Above MAP. Shopify will not bill an added line over ' + money(_map)
                 + ' — lower the points, or raise the price in Shopify first.')
              : '';
          }
        }

        _derive();
        function _reprice() { _derive(); clearTimeout(_debounce); _debounce = setTimeout(refresh, 350); }
        if (ptsEl) ptsEl.addEventListener('input', _reprice);
        if (priceEl && !_canPts) priceEl.addEventListener('input', _reprice);
        qtyEl.addEventListener('input', _reprice);
        // Immediate, not debounced: a tick flips the whole adjustment between "bill them" and
        // "give it to them", and the preview is the only place that difference is visible.
        if (freeEl) freeEl.addEventListener('change', function () { _derive(); refresh(); });
      } else {
        tr.innerHTML = '<td>' + esc(v.label) + (v.sku ? '<span class="rqd-ao__muted"> · ' + esc(v.sku) + '</span>' : '')
          + '<span class="rqd-ao__muted"> · ' + money(v.price) + ' ea</span></td>'
          + (_famPick
            ? '<td class="ta-r"><select class="rqd-ao__addorder" title="Which order this item goes on. Auto puts it on the order being reduced, so the change settles as one movement.">'
              + '<option value="">Auto</option>'
              + _famOrders.map(function (o) { return '<option value="' + esc(o.orderName) + '">' + esc(o.orderName) + '</option>'; }).join('')
              + '</select></td>'
            : '')
          + '<td class="ta-r"><input type="number" min="1" step="1" class="rqd-ao__addqty" value="1" style="width:64px;"></td>'
          /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — give it away. Ticked, the item still
             goes onto the customer's real order (so it is picked, shipped, and lands on the vendor
             PO like anything else) but the money does not move: the worker zeroes the line with a
             100% discount, the order total is unchanged, no balance is raised and no email is sent. */
          + '<td class="ta-r"><label class="rqd-ao__nocharge" title="Put this item on the order and give it away. The order total does not change and the customer is not emailed."><input type="checkbox" class="rqd-ao__addfree"> No charge</label></td>'
          + '<td class="ta-r"><button type="button" class="rqd-ao__link-btn rqd-ao__addrm">Remove</button></td>';
        addedTbl.querySelector('tbody').appendChild(tr);
        tr.querySelector('.rqd-ao__addqty').addEventListener('input', function () {
          clearTimeout(_debounce); _debounce = setTimeout(refresh, 350);
        });
        tr.querySelector('.rqd-ao__addfree').addEventListener('change', refresh);
      }

      var _pick = tr.querySelector('.rqd-ao__addorder');
      if (_pick) _pick.addEventListener('change', refresh);
      /* ⛔ REMOVING A SWAP ROW PUTS THE ORIGINAL LINE BACK. Leaving its qty box at 0 would turn an
         abandoned exchange into a silent deletion of the customer's item — the one mistake this
         control could make that nobody would see on the way to Commit. */
      tr.querySelector('.rqd-ao__addrm').addEventListener('click', function () {
        var _of = tr.getAttribute('data-swap-of');
        if (_of) {
          var back = body.querySelector('.rqd-ao__qty[data-line="' + _cssEsc(_of) + '"]');
          if (back) {
            back.value = back.getAttribute('data-from');
            var row = body.querySelector('tr[data-line-row="' + _cssEsc(_of) + '"]');
            if (row) row.classList.remove('rqd-ao__lines-row--swapped');
          }
        }
        tr.parentNode.removeChild(tr); syncAddedVisibility(); refresh();
      });
      syncAddedVisibility();
      refresh();
    }

    function runSearch() {
      var q = (addQ.value || '').trim();
      if (q.length < 2) { addResults.hidden = true; addResults.innerHTML = ''; return; }
      addResults.hidden = false;
      addResults.innerHTML = '<div class="rqd-ao__muted">Searching…</div>';
      api().get('/product-search?q=' + encodeURIComponent(q))
        .then(function (r) {
          var products = (r && r.products) || [];
          /* CUSTOM_AOSEARCH_BY_CLAUDE (Ryan 2026-08-29) — ⛔ MATCH THE WORDS AGAINST THE ROW, NOT
             JUST AGAINST THE PRODUCT.

             Two complaints, one cause. Ryan typed `weld rod` and got **Legend Fitness 3124 Smith
             Machine**; he typed `Catalina` and got the Catalina laminate roll (right), then every
             OTHER colour of that roll, then every colour of the weld rods.

             ⚠️ `/product-search` hands the raw string to Shopify's full-text search, which reads
             the product DESCRIPTION — and every Legend machine's description mentions a welded
             frame and guide rods. Measured on the live catalogue: a bare `weld rod` returns six
             Legend machines in positions 1-6 and the seven real ECORE weld rods in 7-13.

             ⚠️ And COLOUR IS A VARIANT, NOT A PRODUCT. One product matching on a colour option
             was flattened to all ~20 of its colour variants, so asking for Catalina listed
             nineteen colours that are not Catalina. The old comment here saw the volume and
             capped it at 50; the rows were never the problem, the relevance was.

             So every word must appear somewhere in the ROW's own label (product title + variant
             title). Legend machines have neither "weld" nor "rod" in their titles and drop out;
             the non-Catalina variants drop out; `bounce 2 weld rod catalina` narrows to one.

             ⛔ AND IT FALLS BACK RATHER THAN SHOWING NOTHING. If no row matches every word — a
             term that only ever appears in a description, or matches pushed past the endpoint's
             20-product cap — the unfiltered rows are shown, which is exactly today's behaviour.
             A filter that can return an empty list is worse than the noise it replaces. */
          var _res = variantRowsFor(q, products);
          var rows = _res.rows;
          var all = { length: _res.total };
          var _words = _res.words;
          var _fellBack = _res.fellBack;
          if (!rows.length) { addResults.innerHTML = '<div class="rqd-ao__muted">No matches.</div>'; return; }
          var html = '';
          rows.forEach(function (v, i) {
            html += '<button type="button" class="rqd-ao__addhit" data-i="' + i + '">'
              + esc(v.label) + (v.sku ? '<span class="rqd-ao__muted"> · ' + esc(v.sku) + '</span>' : '')
              + '<span class="rqd-ao__muted"> · ' + money(v.price) + '</span></button>';
          });
          if (rows.length >= 50) html += '<div class="rqd-ao__muted">Showing the first 50 — narrow the search (try the SKU).</div>';
          /* ⚠️ SAY WHEN THE LIST IS THE UNFILTERED FALLBACK. Otherwise the two states look
             identical and a page of near-misses reads as "these all matched" — which is the
             confusion that started this ("weld rod" -> Smith Machine, with nothing saying why). */
          else if (_fellBack && _words.length && all.length) {
            html += '<div class="rqd-ao__muted">Nothing matched every word in the title or SKU — '
              + 'showing what the catalogue search returned for “' + esc(q) + '”.</div>';
          }
          addResults.innerHTML = html;
          addResults.querySelectorAll('.rqd-ao__addhit').forEach(function (btn) {
            btn.addEventListener('click', function () {
              addRow(rows[parseInt(btn.getAttribute('data-i'), 10)]);
              addResults.hidden = true; addResults.innerHTML = '';
              addQ.value = '';
            });
          });
        })
        .catch(function (e) {
          addResults.innerHTML = '<div class="rqd-ao__err">' + esc(e.message || 'Search failed') + '</div>';
        });
    }

    /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — THE SWAP PICKER.

       ⛔ THE SAME PRODUCT'S OTHER VARIANTS COME FIRST, and that is not a convenience.
       A colour change is almost always the same product in another colour, and the catalogue
       search flattens every product to every variant — so asking an admin to find
       "Performance Rally Tile — Black" among twenty near-identical rows is how the wrong
       colour gets ordered. `/product-by-handle` answers with exactly this product's variants;
       the search box below is the fallback for a genuine product change.

       ⚠ THE VARIANT BEING REPLACED IS FILTERED OUT. Offering it back as its own
       replacement is the one answer the list must never give — the Discontinued card learned
       the same lesson on 2026-08-31, for the same reason. */
    /* The line payload behind a qty row, by its Shopify line id. `ordersOf` is the same list the
       qty table was rendered from, so this cannot disagree with what is on screen. */
    function _lineByIdFor(lineId) {
      var found = null;
      ordersOf(c).forEach(function (o) {
        (o.lineItems || []).forEach(function (li) {
          if (li && li.lineItemId === lineId) found = li;
        });
      });
      return found;
    }

    function openSwapPicker(btn) {
      var lineId = btn.getAttribute('data-line');
      var handle = btn.getAttribute('data-handle') || '';
      var current = btn.getAttribute('data-variant') || '';
      var label = btn.getAttribute('data-label') || 'this item';
      var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
      var host = btn.closest('tr');
      var open = body.querySelector('.rqd-ao__swappick');
      if (open) {
        var was = open.getAttribute('data-for');
        open.parentNode.removeChild(open);
        if (was === lineId) return;   // the button is a toggle
      }
      var pick = document.createElement('tr');
      pick.className = 'rqd-ao__swappick';
      pick.setAttribute('data-for', lineId);
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — ⛔ THE PICKER SEARCHES THE WHOLE
         CATALOGUE, not just this product's other colours.

         The first cut listed same-product variants and, for anything else, pointed at the Add
         box below. That was wrong twice over. A line quoted by the sq ft — which is most of
         the ECORE flooring business — reaches the ORDER as a CUSTOM line with no variant and
         no handle, so there was no product to list colours from and the pointer was the only
         path left. And a row added through the Add box carries no `swapOf`, so the worker
         refused the very case the pairing exists to allow: swapping UP to a dearer item came
         back `blocked-mixed-collect`.

         So the search lives HERE, and whatever it returns is paired with the line being
         replaced. Ryan: *"I need to be able to sub out one product completely, for another
         one.. same qty, different sku."* The same-product variants still come first when the
         line has a handle, because a colour change is the common case and they cost one call. */
      pick.innerHTML = '<td colspan="4"><div class="rqd-ao__swappick-in">'
        + '<div class="rqd-ao__muted">Replace <strong>' + esc(label) + '</strong> with:</div>'
        + '<input type="search" class="rqd-ao__swapq" autocomplete="off" '
        + 'placeholder="Search the catalogue by SKU or title…" style="width:100%;box-sizing:border-box;margin-top:6px;">'
        + '<div class="rqd-ao__swaphits">Loading…</div>'
        + '<div class="rqd-ao__muted rqd-ao__swaphint"></div>'
        + '</div></td>';
      host.parentNode.insertBefore(pick, host.nextSibling);
      var hits = pick.querySelector('.rqd-ao__swaphits');

      function render(rows) {
        if (!rows.length) {
          hits.innerHTML = '<div class="rqd-ao__muted">No other variant of this product — '
            + 'search above for a different product.</div>';
          return;
        }
        var html = '';
        rows.forEach(function (v, i) {
          html += '<button type="button" class="rqd-ao__addhit" data-i="' + i + '">'
            + esc(v.label) + (v.sku ? '<span class="rqd-ao__muted"> · ' + esc(v.sku) + '</span>' : '')
            + '<span class="rqd-ao__muted"> · ' + money(v.price) + '</span></button>';
        });
        hits.innerHTML = html;
        hits.querySelectorAll('.rqd-ao__addhit').forEach(function (b) {
          b.addEventListener('click', function () {
            var chosen = rows[parseInt(b.getAttribute('data-i'), 10)];
            /* The DOWN-leg is an ordinary qty change, typed by us instead of by them. That is
               what lets readForm stay one reader and the worker see one adjustment. */
            var qbox = body.querySelector('.rqd-ao__qty[data-line="' + _cssEsc(lineId) + '"]');
            if (qbox) qbox.value = '0';
            if (host) host.classList.add('rqd-ao__lines-row--swapped');
            /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — the outgoing line's own numbers
               travel with the pairing. `quote` is the MSRP / MAP / cost / points the rep typed
               on the Workstation, attached to the line by the worker for a human admin only
               (see quoteFiguresFor). Without it the row can still price the incoming item; it
               just cannot show what the outgoing one earned, which is half of what Ryan asked
               to be able to see. */
            var outLine = _lineByIdFor(lineId);
            addRow(chosen, {
              lineItemId: lineId, label: label, qty: qty,
              unitPrice: outLine ? outLine.unitPrice : null,
              quote: outLine ? outLine.quote : null,
            });
            if (pick.parentNode) pick.parentNode.removeChild(pick);
          });
        });
      }

      var hint = pick.querySelector('.rqd-ao__swaphint');
      var qBox = pick.querySelector('.rqd-ao__swapq');
      var _pickDebounce;

      function runPickSearch() {
        var term = (qBox.value || '').trim();
        if (term.length < 2) { hint.textContent = 'Type at least two characters.'; return; }
        hits.innerHTML = '<div class="rqd-ao__muted">Searching…</div>';
        hint.textContent = '';
        api().get('/product-search?q=' + encodeURIComponent(term))
          .then(function (r) {
            var res = variantRowsFor(term, (r && r.products) || []);
            /* ⚠ The line being replaced is filtered out here too: offering it back as its
               own replacement is the one answer this list must never give. */
            var rows = res.rows.filter(function (v) { return String(v.id) !== String(current); });
            render(rows);
            if (!rows.length) hint.textContent = 'No matches.';
            else if (res.fellBack && res.total) {
              hint.textContent = 'Nothing matched every word in the title or SKU — showing what '
                + 'the catalogue search returned for “' + term + '”.';
            }
          })
          .catch(function (e) {
            hits.innerHTML = '<div class="rqd-ao__err">' + esc(e.message || 'Search failed') + '</div>';
          });
      }
      qBox.addEventListener('input', function () {
        clearTimeout(_pickDebounce); _pickDebounce = setTimeout(runPickSearch, 300);
      });
      qBox.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(_pickDebounce); runPickSearch(); }
      });

      if (!handle) {
        /* A custom or sq-ft line has no product to enumerate. That is no longer a dead end —
           the search above replaces it with anything in the catalogue, still paired. */
        hits.innerHTML = '';
        hint.textContent = 'This line has no catalogue product behind it, so search for the '
          + 'replacement above.';
        qBox.focus();
        return;
      }
      api().get('/product-by-handle?handle=' + encodeURIComponent(handle))
        .then(function (r) {
          var p = (r && r.product) || null;
          var vs = (p && p.variants) || [];
          var rows = vs.filter(function (v) {
            if (String(v.id) === String(current)) return false;   // never offer it back to itself
            return v.available !== false;
          }).map(function (v) {
            var vt = (v.title && v.title !== 'Default Title') ? v.title : '';
            return { id: v.id, sku: v.sku, price: v.price, msrp: v.compareAtPrice || null,
              cost: v.cost || null, label: (p.title || '') + (vt ? ' — ' + vt : '') };
          });
          render(rows);
        })
        .catch(function (e) {
          hits.innerHTML = '<div class="rqd-ao__err">' + esc(e.message || 'Could not load this product') + '</div>';
        });
    }

    body.querySelectorAll('.rqd-ao__swapbtn').forEach(function (btn) {
      btn.addEventListener('click', function () { openSwapPicker(btn); });
    });

    if (addQ) {
      addQ.addEventListener('input', function () { clearTimeout(_addDebounce); _addDebounce = setTimeout(runSearch, 300); });
      // Enter would submit the surrounding form context; keep it as an explicit search.
      addQ.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(_addDebounce); runSearch(); }
      });
    }
    /* Refund mode drives which field is relevant — show only that one, so the form can't
       be filled in a way the chosen mode ignores. Selects fire 'change', not 'input'. */
    /* Per REFUND BLOCK now — there is one per order in the family, and each has its own mode
       select, its own fields and its own line list. Scoping the field-sync to `blk` is what
       keeps one order's chosen mode from hiding another order's inputs. */
    body.querySelectorAll('.rqd-ao__refund[data-order]').forEach(function (blk) {
      var rmodeSel = blk.querySelector('.rqd-ao__rmode');
      if (!rmodeSel) return;
      var syncRefundFields = function () {
        var m = rmodeSel.value;
        var show = {
          amount: m === 'flat',
          percent: m === 'line-percent' || m === 'order-percent',
          line: m === 'line' || m === 'line-percent',
        };
        blk.querySelectorAll('.rqd-ao__rfield').forEach(function (el) {
          var kind = el.classList.contains('rqd-ao__rfield--amount') ? 'amount'
            : (el.classList.contains('rqd-ao__rfield--percent') ? 'percent' : 'line');
          el.hidden = !show[kind];
        });
      };
      rmodeSel.addEventListener('change', function () { syncRefundFields(); refresh(); });
      var rline = blk.querySelector('.rqd-ao__rline');
      if (rline) rline.addEventListener('change', refresh);
      syncRefundFields();
    });

    commitBtn.addEventListener('click', function () {
      if (!_lastAdj || !familyCommittable(_lastAdj)) return;
      var reason = (body.querySelector('.rqd-ao__reason').value || '').trim();
      doCommit(c, readForm(body, c), reason, _lastAdj);
    });
  }

  /* Is this family preview safe to commit? Every leg must be on a committable rail AND the
     family must not be moving money both ways.
     ⛔ ONE predicate, consulted by BOTH the button's enabled state and the click handler —
     the lesson recorded at the COMMITTABLE_RAILS note above, which cost an enabled button that
     silently did nothing. The worker keeps its own independent copy on purpose (never trust the
     client), but on this side there is exactly one. */
  function familyCommittable(res) {
    if (!res || !res.rollup) return false;
    if (res.rollup.errors && res.rollup.errors.length) return false;
    if (res.rollup.conflict) return false;
    var legs = (res.perOrder || []).filter(function (p) { return p && p.adjustment; });
    if (!legs.length) return false;
    return legs.every(function (p) { return isCommittableRail(p.adjustment.rail); });
  }

  function renderPreview(el, commitBtn, adj, impact) {
    var rows = '';
    /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — A SWAP READS AS ONE LINE, because it
       IS one decision. Printed as its two legs it becomes a credit and a charge for
       near-identically named products, and the thing the admin has to confirm — that the
       right colour is going and the right colour is coming — is the hardest fact to see on
       the screen. So the down-leg is folded into the addition's row and the NET is what shows.
       `swapOf` is asserted by the panel and validated by the worker, so a row can only pair
       with a line this same adjustment is really changing. */
    var _swapPairs = {};
    (adj.additions || []).forEach(function (a) { if (a && a.swapOf) _swapPairs[a.swapOf] = true; });
    (adj.lines || []).forEach(function (l) {
      if (_swapPairs[l.lineItemId]) return;   // shown on its swap row instead
      var credit = l.delta < 0;
      rows += '<tr><td>' + esc(l.title) + ': ' + l.fromQty + ' → ' + l.toQty + '</td>'
        + '<td class="ta-r ' + (credit ? 'rqd-ao__credit' : 'rqd-ao__charge') + '">'
        + (credit ? '−' : '+') + money(Math.abs(l.delta)) + '</td></tr>';
    });
    /* Added SKUs. Always a charge — you can't add a negative quantity — so no credit branch.
       Shows the resolved title/SKU the WORKER looked up, not what the picker displayed, so
       the admin is confirming the item Shopify will actually charge for. */
    (adj.additions || []).forEach(function (a) {
      /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — a give-away has to READ like one. It
         settles at +$0.00, and a row saying "+$0.00" next to a real product is the kind of line an
         admin scans straight past on the way to Commit; what they need to confirm is that the
         customer is not being billed AND what the gesture costs us. So: the word FREE where the
         charge would be, and the list value named as what is being given away. */
      if (a.noCharge) {
        rows += '<tr><td>Add: ' + esc(a.title || a.sku || 'item')
          + '<span class="rqd-ao__muted"> · ' + a.quantity + ' × ' + money(a.unitPrice)
          + ', given at no charge</span></td>'
          + '<td class="ta-r rqd-ao__credit">FREE</td></tr>';
        return;
      }
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — the exchange, with the money it
         nets to. The price shown is what we are actually charging (`effectiveUnitPrice`), with
         the variant's own price beside it when the admin has priced it down — otherwise the
         row would claim a figure the customer is not being billed. Margin only when we know
         the cost: a blank is honest and a 0 is not. */
      if (a.swapOf) {
        var _out = (adj.lines || []).filter(function (l) { return l.lineItemId === a.swapOf; })[0];
        var _net = (a.delta || 0) + (_out ? (_out.delta || 0) : 0);
        var _eff = (a.effectiveUnitPrice != null) ? a.effectiveUnitPrice : a.unitPrice;
        var _priced = (a.targetUnitPrice != null);
        rows += '<tr><td>' + (_out ? esc(_out.title) + ' → ' : 'Swap in ')
          + esc(a.title || a.sku || 'item')
          + '<span class="rqd-ao__muted"> · ' + a.quantity + ' × ' + money(_eff)
          + (_priced ? ' (was ' + money(a.unitPrice) + ')' : '')
          + (a.points != null ? ' · ' + a.points.toFixed(1) + ' pts' : '')
          + '</span></td>'
          + '<td class="ta-r ' + (_net < 0 ? 'rqd-ao__credit' : (_net > 0 ? 'rqd-ao__charge' : 'rqd-ao__muted')) + '">'
          + (Math.abs(_net) < 0.005 ? 'even' : ((_net < 0 ? '−' : '+') + money(Math.abs(_net))))
          + '</td></tr>';
        return;
      }
      rows += '<tr><td>Add: ' + esc(a.title || a.sku || 'item')
        + '<span class="rqd-ao__muted"> · ' + a.quantity + ' × ' + money(a.unitPrice) + '</span></td>'
        + '<td class="ta-r rqd-ao__charge">+' + money(a.delta) + '</td></tr>';
    });
    if (adj.freight && adj.freight.delta) {
      var fcredit = adj.freight.delta < 0;
      rows += '<tr><td>Freight: ' + money(adj.freight.fromCharged) + ' → ' + money(adj.freight.toCharged) + '</td>'
        + '<td class="ta-r ' + (fcredit ? 'rqd-ao__credit' : 'rqd-ao__charge') + '">'
        + (fcredit ? '−' : '+') + money(Math.abs(adj.freight.delta)) + '</td></tr>';
    }
    /* Direct refund — show what the number was derived FROM (the mode's basis), so a
       %-based refund is auditable at a glance instead of appearing as a bare figure. */
    if (adj.manualRefund) {
      rows += '<tr><td>Direct refund<span class="rqd-ao__muted">'
        + (adj.manualRefund.basisLabel ? ' · ' + esc(adj.manualRefund.basisLabel) : '') + '</span></td>'
        + '<td class="ta-r rqd-ao__credit">−' + money(adj.manualRefund.amount) + '</td></tr>';
      if (adj.manualRefund.clamped) {
        rows += '<tr><td colspan="2" class="rqd-ao__muted">Capped at the ' + money(adj.manualRefund.cap)
          + ' still refundable'
          + (adj.manualRefund.alreadyRefunded > 0 ? ' (' + money(adj.manualRefund.alreadyRefunded) + ' already refunded)' : '')
          + '.</td></tr>';
      }
    }

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-05) — Who this costs, BEFORE committing.
       A refund lowers GP, which lowers the rep / CFA / referral entitlements. Anyone ALREADY
       PAID on the old GP isn't clawed back — the difference goes onto their next statement as a
       deduction — so it must be visible here rather than discovered later on the Commissions
       page. Absent (null) for every non-refund rail, and for deals with no entitlements. */
    if (impact && ((impact.deductions || []).length || (impact.lowered || []).length)) {
      var ENT_LABEL = { rep: 'Rep commission', cfa: 'CFA commission', referral: 'Referral' };
      rows += '<tr><td colspan="2" class="rqd-ao__muted" style="padding-top:8px;">'
        + 'GP of goods ' + money(impact.gpBefore) + ' → ' + money(impact.gpAfter)
        + ' (−' + money(impact.gpDrop) + ')</td></tr>';
      (impact.lowered || []).forEach(function (l) {
        rows += '<tr><td>' + esc(ENT_LABEL[l.key] || l.key) + ' recalculated'
          + '<span class="rqd-ao__muted"> · not yet paid</span></td>'
          + '<td class="ta-r rqd-ao__credit">' + money(l.was) + ' → ' + money(l.becomes) + '</td></tr>';
      });
      (impact.deductions || []).forEach(function (d) {
        rows += '<tr><td>' + esc(ENT_LABEL[d.type] || d.type) + ' — ' + esc(d.recipient)
          + '<span class="rqd-ao__muted"> · already paid ' + money(d.was)
          + ', deducted from their next statement</span></td>'
          + '<td class="ta-r rqd-ao__charge">' + money(d.amount) + '</td></tr>';
      });
    }

    var isReduce = adj.rail === 'reduce';
    var netLabel, netStr, banner = '';
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — a reduce is still money owed, so it is
       type 'invoice', but "Net owed by customer" reads as though we are asking for MORE. What the
       admin is looking at is the balance after the reduction. */
    if (isReduce) { netLabel = 'New balance owed'; netStr = money(adj.settlement.amount); }
    else if (adj.settlement.type === 'refund') { netLabel = 'Net refund to customer'; netStr = money(adj.settlement.amount); }
    else if (adj.settlement.type === 'invoice') { netLabel = 'Net owed by customer'; netStr = money(adj.settlement.amount); }
    else { netLabel = 'Net change'; netStr = money(0); }

    // Rail-driven CTA state. order-edit = real order edit; freight-refund = a pure shipping
    // refund (no edit); collect = edit the order UP + email its pay-link; reduce = edit the order
    // DOWN, leaving a smaller balance on the same pay-link. All commit here.
    var isFreightRefund = adj.rail === 'freight-refund';
    var isCollect = adj.rail === 'collect';
    var isManualRefund = adj.rail === 'refund-only';
    if (isCommittableRail(adj.rail)) {
      commitBtn.disabled = false;
      commitBtn.textContent = isReduce
        ? 'Commit adjustment (then email the revised ' + money(adj.settlement.amount) + ' balance)'
        : (isCollect
        ? 'Commit adjustment (then email the ' + money(adj.settlement.amount) + ' pay-link)'
        : (adj.settlement.type === 'refund'
          ? 'Commit adjustment (then release ' + money(adj.settlement.amount) + ')'
          : 'Commit adjustment'));
    } else {
      commitBtn.disabled = true;
      commitBtn.textContent = 'Commit adjustment';
      if (adj.rail === 'blocked-mixed-collect') {
        banner = '<div class="rqd-ao__banner">This nets to money owed but also lowers a line — that mix can’t be '
          + 'settled automatically. Handle it in Shopify / the Quote Workstation, or split it in two.</div>';
      } else if (adj.rail === 'blocked-fulfilled') {
        banner = '<div class="rqd-ao__banner">A changed line has already shipped — Shopify won’t let us edit it. '
          + 'To hand money back on a shipped order, clear the quantity change and use '
          + '<strong>Direct refund</strong> above instead.</div>';
      } else if (adj.rail === 'blocked-mixed-refund') {
        banner = '<div class="rqd-ao__banner">A direct refund can’t be combined with line or freight changes — the same '
          + 'money would move twice. Commit one, then the other.</div>';
      } else if (adj.rail === 'noop') {
        banner = '<div class="rqd-ao__muted">No changes yet.</div>';
      }
    }
    if ((adj.errors || []).length) {
      banner = '<div class="rqd-ao__banner rqd-ao__banner--err">' + adj.errors.map(esc).join('<br>') + '</div>';
      commitBtn.disabled = true;
    }

    var taxNote = ((adj.rail === 'order-edit' || isFreightRefund) && adj.settlement.type === 'refund')
      ? '<div class="rqd-ao__muted">Estimate — the final refund uses Shopify’s tax-recalculated figure, so it may differ by a few cents.</div>'
      : (isReduce
        ? '<div class="rqd-ao__muted">Estimate — the new balance uses Shopify’s tax-recalculated total, so it may differ by a few cents. '
          + 'Nothing is refunded: none of this has been paid yet, so the customer simply owes less on the same pay-link.</div>'
      : (isCollect
        ? '<div class="rqd-ao__muted">Estimate — the balance the customer pays uses Shopify’s tax-recalculated total, so it may differ by a few cents.</div>'
        : ''));
    // A freight refund doesn't change the order total — the overcharged shipping is refunded
    // separately — so don't imply a new order total on that rail.
    var totalLine = isFreightRefund
      ? '<div class="rqd-ao__muted">Order total stays ' + money(adj.currentTotal) + ' — overcharged shipping is refunded to the customer.</div>'
      : '<div class="rqd-ao__muted">Order total ' + money(adj.currentTotal) + ' → ' + money(adj.projectedTotal) + '</div>';
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — returns its HTML as well as writing it.
       A family preview renders this same body once PER ORDER and stacks the results under order
       headings, so it needs the markup rather than a side effect on `el`. `el` may be null in
       that mode; `commitBtn` may be a stub the caller inspects instead of a real button. Every
       branch above is untouched — one order's preview reads exactly as it always did. */
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — say the quiet part. Every other rail ends
       in something leaving the building (a refund, a pay-link, an email), so the one rail that
       deliberately does NONE of that has to say so — otherwise the admin has no way to tell an
       adjustment that will stay silent from one that is about to mail the customer, and the only
       difference on screen is a $0.00 they cannot distinguish from "not calculated yet". */
    var _freeAdds = (adj.additions || []).filter(function (a) { return a && a.noCharge; });
    var freeNote = (_freeAdds.length && adj.settlement.type === 'none')
      ? '<div class="rqd-ao__muted">Nothing is charged and the customer is not emailed. '
        + (_freeAdds.length === 1 ? 'The item goes' : 'The items go')
        + ' onto their order at no cost, and will appear on the vendor PO so the supplier ships '
        + (_freeAdds.length === 1 ? 'it' : 'them') + '.</div>'
      : '';

    var out = banner
      + '<table class="rqd-ao__delta"><tbody>' + rows
      + '<tr class="rqd-ao__net"><td>' + esc(netLabel) + '</td><td class="ta-r">' + netStr + '</td></tr>'
      + '</tbody></table>'
      + totalLine
      + freeNote
      + taxNote;
    if (el) el.innerHTML = out;
    return out;
  }

  /* ── Family preview ───────────────────────────────────────────────────────
     One block per order that is actually changing, plus the thing no per-order view can show:
     what the CUSTOMER ends up experiencing. */
  function renderFamilyPreview(el, commitBtn, res, c) {
    var roll = res.rollup || {};
    var legs = (res.perOrder || []).filter(function (p) { return p && p.adjustment; });
    var many = ordersOf(c).length > 1;
    var h = '';

    /* Where each added item landed. Shown whenever the worker steered one, because a silent
       placement is a decision the admin never saw — and on a family it decides whether they get
       one movement or two. */
    var steered = (res.placements || []).filter(function (p) { return p.steered; });
    if (many && (res.placements || []).length) {
      h += '<div class="rqd-ao__placements">';
      (res.placements || []).forEach(function (p) {
        h += '<div class="rqd-ao__muted">Added item → <strong>' + esc(p.orderName) + '</strong>'
          + (p.steered ? ' <span class="rqd-ao__muted">· ' + esc(p.reason) + '</span>' : ' · you chose this')
          + '</div>';
      });
      h += '</div>';
    }

    if (roll.conflict) {
      /* ⛔ The one thing this panel cannot do, said plainly and with the cause named. Shopify
         settles per order: one order's refund cannot pay another's balance, so this would reach
         the customer as a refund AND a bill for a single change of mind. */
      var causes = (roll.conflictDetail && roll.conflictDetail.causes) || [];
      h += '<div class="rqd-ao__banner rqd-ao__banner--err">'
        + '<strong>These changes move money both ways.</strong><br>'
        + causes.map(function (cz) {
          return esc(cz.orderName) + ' would ' + (cz.direction === 'in' ? 'collect' : 'refund')
            + (cz.lines.length ? ' — ' + esc(cz.lines.join(', ')) : '');
        }).join('<br>')
        + '<br><br>Shopify settles money per order, so one order\'s refund can\'t pay the other\'s balance — '
        + 'the customer would get a refund and a bill for the same change of mind. '
        + 'Put the added items on the order you\'re reducing, or make the quantity changes on one order only.'
        + '</div>';
    }

    legs.forEach(function (leg) {
      var stub = {};
      var impact = (res.commissionImpacts || []).filter(function (ci) { return ci.orderName === leg.orderName; })[0];
      if (many) h += '<div class="rqd-ao__ordergroup">' + esc(leg.orderName) + '</div>';
      h += renderPreview(null, stub, leg.adjustment, impact && impact.impact);
    });

    (res.perOrder || []).filter(function (p) { return p && p.error; }).forEach(function (p) {
      h += '<div class="rqd-ao__banner rqd-ao__banner--err">' + esc(p.orderName) + ': ' + esc(p.error) + '</div>';
    });

    /* The honest pair. familyNet is the number to think in; `movements` is what the customer
       actually receives. They are the same thing only when there is one movement — saying
       "net $346.20" while two separate figures hit their card and their inbox would be a
       number that appears on nothing they get. */
    if (many && (roll.movements || []).length) {
      h += '<table class="rqd-ao__delta rqd-ao__famnet"><tbody>';
      (roll.movements || []).forEach(function (m) {
        h += '<tr><td>' + esc(m.orderName) + ' — ' + (m.direction === 'in' ? 'customer pays' : 'refund to customer') + '</td>'
          + '<td class="ta-r ' + (m.direction === 'in' ? 'rqd-ao__charge' : 'rqd-ao__credit') + '">'
          + (m.direction === 'in' ? '+' : '−') + money(m.amount) + '</td></tr>';
      });
      h += '<tr class="rqd-ao__net"><td>'
        + (roll.singleMovement
          ? 'The customer sees ONE movement'
          : 'The customer sees ' + roll.movements.length + ' SEPARATE movements')
        + '</td><td class="ta-r">' + (roll.singleMovement ? money(Math.abs(roll.familyNet)) : '—') + '</td></tr>';
      h += '</tbody></table>';
      if (!roll.singleMovement) {
        h += '<div class="rqd-ao__muted">Each order settles on its own — they are emailed and released separately.</div>';
      }
    }

    var ok = familyCommittable(res);
    commitBtn.disabled = !ok;
    if (ok) {
      var one = (roll.movements || [])[0];
      commitBtn.textContent = (roll.movements || []).length === 0
        ? 'Commit adjustment'
        : (roll.singleMovement
          ? (one.direction === 'in'
            ? 'Commit adjustment (then email the ' + money(one.amount) + ' balance)'
            : 'Commit adjustment (then release ' + money(one.amount) + ')')
          : 'Commit ' + roll.movements.length + ' orders');
    } else {
      commitBtn.textContent = 'Commit adjustment';
    }
    if (!legs.length && !(roll.errors || []).length) h += '<div class="rqd-ao__muted">No changes yet.</div>';

    el.innerHTML = h;
  }

  /* ── Commit (type-to-confirm the order ref) ────────────────────────────── */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — `res` is now the FAMILY preview result.
     For the single-order case (which is nearly every deal) there is one leg and the wording below
     is unchanged; for a family the confirmation names every order being edited, because
     type-to-confirm is worthless if it doesn't say what is being confirmed. */
  function doCommit(c, input, reason, res) {
    var legs = (res.perOrder || []).filter(function (p) { return p && p.adjustment; });
    var roll = res.rollup || {};
    if (!legs.length) return;

    if (legs.length > 1) {
      var lines = legs.map(function (leg) {
        var s = leg.adjustment.settlement || {};
        var what = s.type === 'refund' ? 'refund ' + money(s.amount)
          : (s.type === 'invoice'
            ? (leg.adjustment.rail === 'reduce' ? 'balance drops to ' + money(s.amount) : 'collect ' + money(s.amount))
            : 'no money moves');
        return '  • ' + leg.orderName + ' — ' + what;
      }).join('\n');
      var typedF = window.prompt(
        'Commit this adjustment across ' + legs.length + ' Shopify orders?\n\n' + lines
        + '\n\nEach order settles on its own — they are emailed and released separately.'
        + '\n\nThey are committed one at a time, money-IN first. If one fails the rest are stopped and reported.'
        + '\n\nType the deal number (' + c.reference + ') to confirm:');
      if (typedF == null) return;
      if (typedF.trim() !== c.reference) { alert('Order number did not match — nothing committed.'); return; }
      return sendCommit(c, input, reason);
    }

    var adj = legs[0].adjustment;
    var typed = window.prompt(
      'Commit this adjustment to Shopify order ' + legs[0].orderName + '?\n\n'
      + (adj.rail === 'refund-only'
        ? 'This records a ' + money(adj.settlement.amount) + ' refund to the customer — it is NOT sent yet; you release it separately after. '
          + 'The order itself is not edited and nothing is restocked. Once released, the deal\'s revenue, GP and commission all drop by this amount.'
        : (adj.rail === 'freight-refund'
        ? 'This records a ' + money(adj.settlement.amount) + ' shipping refund — it is NOT sent yet; you release it separately after. The order itself is not edited.'
        : (adj.rail === 'reduce'
          ? 'This edits the order DOWN. Nothing is refunded — none of this has been paid yet — so the customer’s outstanding balance simply drops to '
            + money(adj.settlement.amount) + ' on the pay-link they already have. Any earlier balance you asked for is retired. You email them the revised figure next.'
        : (adj.rail === 'collect'
          ? 'This edits the order UP so it carries a ' + money(adj.settlement.amount) + ' balance. Nothing is charged now — you email the customer a secure pay-link next, and they pay online.'
          : (adj.settlement.type === 'refund'
            ? 'This edits the order now. The ' + money(adj.settlement.amount) + ' refund is NOT sent yet — you release it separately after.'
            : 'This edits the order now.')))))
      + '\n\nType the order number (' + c.reference + ') to confirm:');
    if (typed == null) return;
    if (typed.trim() !== c.reference) { alert('Order number did not match — nothing committed.'); return; }
    return sendCommit(c, input, reason);
  }

  /* One request for the whole deal, however many orders it turns out to be. The worker fans out
     to the same per-order commit the single-order route uses, money-IN legs first. */
  function sendCommit(c, input, reason) {
    var familyKey = uuid();
    var backdrop = document.querySelector('.rqd-ao-modal');
    if (backdrop) backdrop.classList.add('rqd-ao--busy');
    api().post('/order-adjust/family-commit', {
      orderGid: c.id,
      ref: (c && c.dealRef) || null,
      confirmName: c.reference,
      familyKey: familyKey,
      reason: reason,
      childRefs: c.childRefs || [],
      orders: input.orders,
      addVariants: input.addVariants,
    }).then(function (r) {
      if (!r) throw new Error('Commit failed');
      /* ⛔ A PARTIAL family commit is reported loudly and specifically. There is no cross-order
         transaction in Shopify, so a stop halfway leaves some orders edited and some not — the
         one outcome that must never be summarised as "done". */
      if (r.partial) {
        var okList = (r.results || []).filter(function (x) { return x.ok; }).map(function (x) { return x.orderName; });
        var badList = (r.results || []).filter(function (x) { return !x.ok; })
          .map(function (x) { return x.orderName + ': ' + (x.error || 'failed'); });
        alert('PARTIALLY COMMITTED.\n\nEdited: ' + (okList.join(', ') || 'none')
          + '\n\nNOT edited: ' + badList.join('\n')
          + '\n\nThe orders listed as edited ARE changed in Shopify. Fix the failure and re-commit — '
          + 'the ones already done are idempotent and will not be edited twice.');
      } else if (!r.ok) {
        throw new Error((r.error) || ((r.results || []).map(function (x) { return x.error; }).filter(Boolean).join('; ')) || 'Commit failed');
      } else if (r.warnings && r.warnings.length) {
        // No success pop-up — the panel reopens straight to the pending settlement.
        // Only surface a warning if a KV side-write flagged one.
        alert('Adjusted — note: ' + r.warnings.join('; '));
      }
      refreshCardHistory(c.reference); // update the on-card log
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — and the DEAL, from the server. The
         commit just changed what the customer owns; Order Contents renders from the board row
         and nothing was asking for a fresh one, so it showed the pre-commit lines until the
         2-minute poll — which by then hit the 180s server cache and showed them again.
         Quiet + force: past the cache, without collapsing the detail view. Fire-and-forget on
         purpose — the panel below must reopen whether or not the board answers. */
      if (window.RQD && typeof window.RQD.refreshDealNow === 'function') window.RQD.refreshDealNow();
      openPanel(c.reference); // reopen → shows the pending settlement(s) + Email & Refund
    }).catch(function (e) {
      alert('Commit failed: ' + (e.message || e));
      if (backdrop) backdrop.classList.remove('rqd-ao--busy');
    });
  }

  /* ── Release refund (type-to-confirm the amount) ───────────────────────── */
  function releaseRefund(ref, adjId, amt, orderName) {
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — the ORDER, explicitly.
       `ref` is the DEAL; on a family the settlement being acted on may belong to a balance
       CHILD, whose ledger, pay-link and refundable transactions are its own. Falling back to
       orderNameFor(ref) would aim the call at the parent and act on the wrong order's money.
       Absent (every pre-family caller) it resolves exactly as before. */
    var oName = orderName || orderNameFor(ref);
    var typed = window.prompt('Release the refund of ' + money(amt) + ' to the customer’s card for ' + ref + '?\n\n'
      + 'Type the amount (' + Number(amt).toFixed(2) + ') to confirm:');
    if (typed == null) return;
    if (parseFloat(typed) !== Math.round(Number(amt) * 100) / 100) { alert('Amount did not match — nothing released.'); return; }
    api().post('/order-adjust/release-refund', { orderName: oName, adjustmentId: adjId, confirmAmount: Number(amt) })
      .then(function (r) {
        if (!r || !r.ok) throw new Error((r && r.error) || 'Refund failed');
        alert('Refund of ' + money(amt) + ' released to the customer’s card.');
        refreshCardHistory(ref);
        openPanel(ref);
      })
      .catch(function (e) { alert('Refund failed: ' + (e.message || e)); });
  }

  /* ── Email (preview → composer → send), optionally releasing the refund ────
     refundAmt != null → the composer's REAL Send releases the refund first, then
     sends the email (refund is the concrete promise; never email "you've been
     refunded" for money that didn't move). A Send TEST never refunds. */
  function openEmailComposer(ref, adjId, refundAmt, orderName) {
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-25) — the ORDER, explicitly.
       `ref` is the DEAL; on a family the settlement being acted on may belong to a balance
       CHILD, whose ledger, pay-link and refundable transactions are its own. Falling back to
       orderNameFor(ref) would aim the call at the parent and act on the wrong order's money.
       Absent (every pre-family caller) it resolves exactly as before. */
    var oName = orderName || orderNameFor(ref);
    if (!(window.RQD && typeof window.RQD.openComposeModal === 'function')) {
      alert('Composer unavailable — reload the page.'); return;
    }
    var c = _cache[ref] || {};
    api().post('/order-adjust/email', { orderName: oName, adjustmentId: adjId, preview: true })
      .then(function (pv) {
        if (!pv || pv.success === false) throw new Error((pv && pv.error) || 'Could not build the email');
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-14) — the worker couldn't reconcile the
           itemised table against the outstanding balance (part paid, or a refund landed between
           two collects), so the email carries the balance + pay-link and NO line items. Say so
           before it opens — the alternative is a silent, wrong money table. */
        if (pv.warning) alert('Heads up before you send:\n\n' + pv.warning);
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — say it will thread BEFORE the composer
           opens. The subject box is about to be inert (Graph's createReply owns "RE: ..."), and a
           subject field you can type into but which is ignored is the kind of thing that reads as a
           bug months later. Also states the balance change, which is the whole point of the email. */
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — ⛔ THE alert() IS GONE. It existed to
           announce "this will REPLY on the existing thread, and your subject edits are ignored",
           which was a modal apologising for state the composer did not show. The composer now shows
           the thread itself, says which message it is answering, and offers the override — so the
           thing the alert was describing is on screen instead of in front of it.

           The thread comes from ABBY'S MAILBOX (/abby/thread), not the deal feed: on SHOP-1941 the
           feed holds three internal notes and zero emails while a four-message conversation plainly
           exists in orders@. Best-effort — a failure hands the composer an `unavailable` note rather
           than an empty list, because "no thread" and "could not look" must not render alike. */
        closeModal(); // composer mounts at the same layer; close ours so it's visible
        var _threadReq = pv.replyToMessageId
          ? api().get('/abby/thread?ref=' + encodeURIComponent(oName)
              + '&messageId=' + encodeURIComponent(pv.replyToMessageId)
              + '&customerEmail=' + encodeURIComponent(pv.customerEmail || ''))
              .catch(function () { return { emails: [], unavailable: 'Could not reach Abby’s mailbox.' }; })
          : Promise.resolve({ emails: [] });
        _threadReq.then(function (th) {
        var _emails = (th && Array.isArray(th.emails)) ? th.emails : [];
        var _otherParty = pv.customerEmail || c.customerEmail || '';
        for (var _ei = 0; _ei < _emails.length; _ei++) {
          if (_emails[_ei] && _emails[_ei].direction === 'inbound') { _otherParty = _emails[_ei].fromName || _emails[_ei].from || _otherParty; break; }
        }
        /* CUSTOM_COLLECTCC_BY_CLAUDE (AIOPS `e65cebdd11b9`, Ryan 2026-09-16) — ⛔ THE Cc IS NO
           LONGER A CONSTANT. It used to be ['ryan@…'] on every collect, with the comment "he's
           referenced in the email" — true when the opener always said so, and false since
           2026-09-08, when that sentence became conditional on the deal feed holding a connected
           call. SHOP-2073 copied him into a conversation he had never had.
           TWO SIGNALS, EITHER OF WHICH KEEPS HIM ON (Ryan's call, 2026-09-16):
             • `pv.ccSuggested` — the worker's answer: a connected call from him on this deal, or
               an offline RW-/JW- deal, whose behaviour this card deliberately did not change.
             • ryan@ already ON THIS EMAIL THREAD — read off the messages the composer has just
               fetched for the reply chooser, so continuity survives even with no call logged.
           ⚠️ THE THREAD SIGNAL IS ONLY AS WIDE AS THE THREAD. `_emails` is empty when this collect
           starts a fresh chain, so a Ryan email on some OTHER thread of the same deal is not seen;
           the call signal is what covers that, and the Cc box is one keystroke ("r") away either
           way. Failing towards an empty Cc is the point of the card. */
        var RYAN_CC = 'ryan@blueskyfitsupply.com';
        var _ccList = (pv && Array.isArray(pv.ccSuggested)) ? pv.ccSuggested.slice() : [];
        if (_ccList.length === 0) {
          for (var _ci = 0; _ci < _emails.length; _ci++) {
            var _em = _emails[_ci] || {};
            var _addrs = [_em.from].concat(_em.to || [], _em.cc || [])
              .map(function (a) { return String(a || '').toLowerCase(); });
            if (_addrs.indexOf(RYAN_CC) !== -1) { _ccList = [RYAN_CC]; break; }
          }
        }
        window.RQD.openComposeModal({
          ref: ref,
          prefillTo: pv.customerEmail || c.customerEmail || '',
          prefillCc: _ccList,
          /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — show the ⏱ tick. Opt-in per composer,
             and this one may opt in now that /order-adjust/email honours the flag.
             ⚠ Ticked by DEFAULT (businessHoursDefault is not set to false): this email tells a
             customer their money moved, and the safe default for that is the next business hour,
             not whatever time the adjustment happened to be committed. */
          showBusinessHours: true,
          prefillSubject: pv.subject || ('Update on your Blue Sky Fitness Supply order - ' + ref),
          // richBody = HTML STRING (contentEditable), NOT prefillBody (Quill) — our
          // email has a table + signature; rich-body preserves both and avoids
          // Quill's lazy-load (the "opens on the 2nd try" bug).
          richBody: pv.html || '',
          fromOptions: ['ABBY'],
          suppressFollowUp: true,
          /* Reply mode + the thread behind it. replyToMessageId is what puts the composer in reply
             mode (and defaults sendMode to 'reply'); replyThread/replyContext feed the scrollable
             preview that has existed since the QW Send-Quote build and that the DD never used. */
          replyToMessageId: pv.replyToMessageId || undefined,
          replyThread: _emails,
          replyContext: _emails.length ? {
            subject: (_emails[0] && _emails[0].subject) || pv.subject || '',
            withWhom: _otherParty,
            count: _emails.length,
          } : undefined,
          replyUnavailable: (th && th.unavailable) || undefined,
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — is there a message Abby may lawfully
             reply to? The worker answers with the same predicate that picks the target, so the box
             the admin sees greyed out and the decision the send makes cannot disagree.
             ⚠️ A thread that came back EMPTY is a no, and so is one full of tests or rep-only
             messages — which is why this is the worker's verdict and not `_emails.length`. */
          canReply: !!(th && th.canReply),
          /* CUSTOM_COLLECTDRIP_BY_CLAUDE (Ryan 2026-09-08) — THE PAY-LINK EMAIL GETS ABBY'S BALANCE
             FOLLOW-UPS, ticked by default, the same box the discontinued notice shows. The worker's
             preview says whether this send would arm one (`dripOffered`: a LIVE collect, the drip
             switched on) — a refund or reduce explanation offers no box. The worker reads `drip`
             on the send and arms lib/collect-drip.js; every outcome lands on the Deal Feed. */
          extraToggle: pv.dripOffered ? {
            label: '⚡ Abby follows up automatically if the balance goes unpaid',
            why: (pv.dripArmed ? 'Follow-ups are already running on this deal; this email re-anchors their clock. ' : '')
              + 'Three more emails in this thread, 3 business days apart, from Abby, each with the live balance and the pay-link. After the second she asks Ryan to call; 3 business days after the last she tells orders@ (cc Ryan) it is time to cancel and refund. A payment, a reply, a logged call or your own email stops it.',
            title: 'On by default. Untick to send this pay-link email without the automatic follow-ups.',
            checked: true,
          } : null,
          sendOverride: function (args) {
            var sendEmail = function () {
              return api().post('/order-adjust/email', {
                orderName: oName, adjustmentId: adjId,
                subject: args.subject, html: args.bodyHtml, richBody: true,
                cc: (args.cc || []).join(','),
                /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — the ⏱ box. Every other customer
                   composer has carried it and this one did not, so a refund note written at 11pm
                   went out at 11pm. ⛔ The worker holds it on the SAME queue an agent's email uses
                   and the five-minute drain releases it in the window — the box exists because the hold
                   does, which is this worker's standing rule about exactly this field. */
                businessHoursOnly: args.businessHoursOnly === true,
                // CUSTOM_COLLECTDRIP_BY_CLAUDE — the box (absent = on, so the worker's default is Ryan's).
                drip: pv.dripOffered ? args.extraToggle !== false : undefined,
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — carry the reply target the
                   preview resolved, so the send threads onto the SAME message the preview was
                   written against. The worker re-resolves it if absent, but passing it removes the
                   window where another email lands between preview and send and silently moves the
                   thread anchor. A test send drops it worker-side. */
                replyToMessageId: pv.replyToMessageId || undefined,
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — 'reply' (default) or the
                   rep's explicit 'new'. Without forwarding it the checkbox would be decorative:
                   this endpoint re-derives a reply target from the adjustment ledger whenever the
                   client sends none, so "new" has to be said out loud. */
                sendMode: args.sendMode || undefined,
                test: !!args.isTest, testRecipient: args.isTest ? (args.to || [])[0] : undefined,
              }).then(function (r) {
                if (!(r && r.success)) throw new Error((r && r.error) || 'Send failed');
                if (!args.isTest) refreshCardHistory(ref);
                return r;
              });
            };
            // Test send never moves money. Email-only mode (no refundAmt) just sends.
            if (args.isTest || refundAmt == null) return sendEmail();
            // Combined: release the refund FIRST, then send the email explaining it.
            return api().post('/order-adjust/release-refund', { orderName: oName, adjustmentId: adjId, confirmAmount: refundAmt })
              .then(function (rr) {
                if (!(rr && rr.ok)) throw new Error((rr && rr.error) || 'Refund failed — email not sent');
                refreshCardHistory(ref);
                return sendEmail().catch(function (ee) {
                  // Money already moved; only the email failed. Don't fail the whole
                  // action — surface it so the admin can resend.
                  alert('Refund of ' + money(refundAmt) + ' was released, but the EMAIL failed to send:\n'
                    + (ee.message || ee) + '\n\nReopen Adjust Order to resend the email.');
                  return { success: true };
                });
              });
          },
        });
        }); // end /abby/thread lookup
      })
      .catch(function (e) { alert('Could not open the email: ' + (e.message || e)); });
  }

  /* ── Public action dispatch (called from dashboard-main.js) ────────────── */
  window.RQD.renderAdjustOrderCard = renderAdjustOrderCard;
  window.RQD.adjustOrder = {
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-07-29) — Open the panel from OUTSIDE the
       detail card (the cog's "Cancel and/or Refund" chooser). _cache is normally seeded as
       a side effect of rendering the card, which hasn't happened when the row is collapsed —
       so seed it from the quote object first, otherwise openPanel alerts "Order data not
       loaded". renderAdjustOrderCard self-gates on online+sold, matching the chooser's own
       gate. */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-05) — `offline` opens the panel on an OFFLINE
       deal's paid order. renderAdjustOrderCard self-gates on source === 'online', so it seeds
       nothing for these rows and openPanel would alert "Order data not loaded". Seed it here
       instead, from the paid order the cog resolved: `reference` is the ORDER name because it's
       the commit confirm-guard, while the cache key stays the deal ref. Line items are left
       empty on purpose — /order-adjust/state refills them from Shopify on open, and a flat
       refund (the offline case Ryan asked for) doesn't need them at all. */
    open: function (ref, q, offline, opts) {
      if (offline && offline.orderGid && !_cache[ref]) {
        _cache[ref] = {
          id: offline.orderGid,
          reference: offline.orderName || '',
          orderName: offline.orderName || '',
          dealRef: offline.dealRef || ref,
          lineItems: [],
          shipping: (q && q.financials && Number(q.financials.freightCharged)) || 0,
          customerEmail: (q && (q.customerEmail || (q.billTo && q.billTo.email))) || '',
          customer: (q && q.customer) || '',
          fulfilled: !!(q && window.RQD.isOrderFullyFulfilled && window.RQD.isOrderFullyFulfilled(q)),
        };
        /* ⚠ Alias the SAME object under the ORDER name too. Everything downstream keys the cache
           by `c.reference` — the post-commit reopen (`openPanel(c.reference)`), the post-release
           reopen, `refreshCardHistory`, and the pending settlement's own data-ref attributes —
           and for an offline deal `c.reference` is the ORDER name, because that's what the commit
           confirm-guard has to match. With only the deal-ref key, committing succeeded and then
           the reopen alerted "Order data not loaded", which reads like the commit failed when it
           hadn't. Two keys → one object fixes every one of those paths at once, and both
           orderNameFor() and c.dealRef still resolve correctly off it. */
        if (offline.orderName && offline.orderName !== ref) {
          _cache[offline.orderName] = _cache[ref];
        }
      }
      if (!_cache[ref] && q) { try { renderAdjustOrderCard(q); } catch (_) { /* fall through to the alert */ } }
      openPanel(ref, opts);
    },
    handle: function (btn) {
      var action = btn.getAttribute('data-adjust-action');
      var ref = btn.getAttribute('data-ref') || '';
      if (action === 'open-panel') openPanel(ref);
      else if (action === 'release-refund') releaseRefund(ref, btn.getAttribute('data-adj'), parseFloat(btn.getAttribute('data-amt')), btn.getAttribute('data-order') || '');
      else if (action === 'email-refund') openEmailComposer(ref, btn.getAttribute('data-adj'), parseFloat(btn.getAttribute('data-amt')), btn.getAttribute('data-order') || '');
      // Collect: email the pay-link. No money "releases" here — the customer pays online
      // and the balance shows collected on the next panel open (totalOutstanding → 0).
      else if (action === 'email-collect') openEmailComposer(ref, btn.getAttribute('data-adj'), null, btn.getAttribute('data-order') || '');
      else if (action === 'email') openEmailComposer(ref, btn.getAttribute('data-adj'), null, btn.getAttribute('data-order') || '');
      else if (action === 'copy-paylink') copyPayLink(btn);
      else if (action === 'undo') undoAdjustments(ref); // CUSTOM_ADJUSTUNDO_BY_CLAUDE
    },
  };

  /* CUSTOM_ADJUSTUNDO_BY_CLAUDE (Ryan 2026-10-06, SHOP-2128) - UNDO ADJUSTMENTS.
     One click asks the worker for a PREVIEW of every order in the deal (/order-adjust/undo without confirm writes
     nothing): what gets undone, the order total now and after, and whether a pay-link already reached the customer.
     Nothing changes until the admin presses Undo in the box. The worker re-checks everything when it runs (a payment
     since, a total that would not land back) and refuses rather than half-doing it. The customer is sent nothing. */
  function undoAdjustments(ref) {
    var targets = _undoTargets[ref] || [];
    if (!targets.length) { alert('Nothing to undo on this order.'); return; }
    var body = openModalShell('Undo adjustments');
    body.innerHTML = '<div class="rqd-ao__undo-loading">Checking what would change…</div>';
    var c = _cache[ref] || {};
    var previews = [];
    var chain = Promise.resolve();
    targets.forEach(function (t) {
      chain = chain.then(function () {
        return api().post('/order-adjust/undo', { orderName: t.orderName, orderGid: t.orderGid, ref: c.dealRef || undefined })
          .then(function (p) { previews.push({ t: t, p: p }); },
            function (e) { previews.push({ t: t, err: (e && e.data && e.data.error) || (e && e.message) || 'Undo is not possible on this order.' }); });
      });
    });
    chain.then(function () { renderUndoConfirm(body, ref, previews); });
  }

  function renderUndoConfirm(body, ref, previews) {
    var ok = previews.filter(function (x) { return !x.err; });
    var h = '<div class="rqd-ao__undo">';
    previews.forEach(function (x) {
      var label = previews.length > 1 ? '<div class="rqd-ao__undo-order">' + esc(x.t.orderName) + '</div>' : '';
      if (x.err) { h += label + '<div class="rqd-ao__err">' + esc(x.err) + '</div>'; return; }
      var p = x.p;
      h += label + '<div class="rqd-ao__label">This puts the order back the way it was before:</div><ul class="rqd-ao__undo-list">'
        + (p.undo || []).map(function (u) { return '<li>' + esc(u.what) + (u.at ? ' <span class="rqd-ao__undo-when">' + esc(fmtDate(u.at)) + '</span>' : '') + '</li>'; }).join('')
        + '</ul>'
        + '<div class="rqd-ao__undo-total">Order total ' + money(p.currentTotal) + ' &rarr; <strong>' + money(p.expectedTotal) + '</strong></div>';
      if (p.emailed && p.emailed.length) {
        h += '<div class="rqd-ao__warn">A pay-link email already went to ' + esc(p.emailed[0].to || 'the customer') + ' on ' + esc(fmtDate(p.emailed[0].sentAt))
          + '. The customer will NOT be emailed about the undo, so let them know the balance is gone.</div>';
      }
      if (p.stopsAt) {
        h += '<div class="rqd-ao__undo-note">Earlier adjustments stay as they are (' + esc(p.stopsAt.words) + ').</div>';
      }
    });
    if (ok.length) {
      h += '<div class="rqd-ao__label">Reason (internal only, optional)</div>'
        + '<textarea class="rqd-ao__reason rqd-ao__undo-reason" rows="2" maxlength="300" placeholder="Why it is being undone."></textarea>'
        + '<div class="rqd-ao__undo-result"></div>'
        + '<div class="rqd-ao__actions"><button type="button" class="rqd-ao__link-btn rqd-ao__undo-cancel">Cancel</button>'
        + '<button type="button" class="rqd-fb-btn rqd-fb-btn--primary rqd-ao__undo-go">Undo adjustments</button></div>';
    } else {
      h += '<div class="rqd-ao__actions"><button type="button" class="rqd-fb-btn rqd-fb-btn--primary rqd-ao__undo-cancel">Close</button></div>';
    }
    h += '</div>';
    body.innerHTML = h;
    var cancel = body.querySelector('.rqd-ao__undo-cancel');
    if (cancel) cancel.addEventListener('click', closeModal);
    var go = body.querySelector('.rqd-ao__undo-go');
    if (!go) return;
    go.addEventListener('click', function () {
      go.disabled = true; go.textContent = 'Undoing…';
      var reasonEl = body.querySelector('.rqd-ao__undo-reason');
      var reason = reasonEl ? reasonEl.value.trim() : '';
      var c = _cache[ref] || {};
      var results = [];
      var chain = Promise.resolve();
      ok.forEach(function (x) {
        chain = chain.then(function () {
          return api().post('/order-adjust/undo', { orderName: x.t.orderName, orderGid: x.t.orderGid, confirm: true, reason: reason || undefined, ref: c.dealRef || undefined })
            .then(function (r) { results.push({ t: x.t, r: r }); },
              function (e) { results.push({ t: x.t, err: (e && e.data && e.data.error) || (e && e.message) || 'Undo failed.' }); });
        });
      });
      chain.then(function () {
        var out = results.map(function (x) {
          var label = results.length > 1 ? esc(x.t.orderName) + ': ' : '';
          if (x.err) return '<div class="rqd-ao__err">' + label + esc(x.err) + '</div>';
          var w = (x.r.warnings || []).map(function (m) { return '<div class="rqd-ao__undo-note">' + esc(m) + '</div>'; }).join('');
          return '<div class="rqd-ao__undo-done">' + label + 'Undone. The order is back to ' + money(x.r.orderTotalAfter) + '.</div>' + w;
        }).join('');
        var res = body.querySelector('.rqd-ao__undo-result');
        if (res) res.innerHTML = out;
        go.textContent = 'Done';
        if (cancel) cancel.textContent = 'Close';
        refreshCardHistory(ref);
        noteDealChanged({ dealChanged: results.some(function (x) { return !x.err; }) });
      });
    });
  }

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — copy the pay-link to the clipboard.
     Confirms IN THE BUTTON rather than via a toast: the button is what the eye is already on,
     and a copy that silently did nothing is indistinguishable from one that worked.

     ⚠️ navigator.clipboard is undefined on a non-secure origin and can be refused by permissions
     policy even on https, so the execCommand path is a real fallback, not ceremony — without it a
     refusal leaves Ryan with no way to get the URL at all, which is worse than the link he had
     before. Restores the label either way so the row never gets stuck reading "Copied". */
  function copyPayLink(btn) {
    var url = btn.getAttribute('data-url') || '';
    if (!url) return;
    var original = btn.textContent;
    var done = function (ok) {
      btn.textContent = ok ? 'Copied ✓' : 'Press Ctrl+C';
      btn.classList.toggle('rqd-ao__link-btn--copied', !!ok);
      setTimeout(function () {
        btn.textContent = original;
        btn.classList.remove('rqd-ao__link-btn--copied');
      }, ok ? 1800 : 3000);
    };
    var legacy = function () {
      try {
        var ta = document.createElement('textarea');
        ta.value = url;
        // Off-screen but focusable — display:none would make select() a no-op.
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(ta);
        done(!!ok);
      } catch (_) { done(false); }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () { done(true); }, legacy);
    } else {
      legacy();
    }
  }

  /* Fill the on-card history after every detail render by wrapping the generic
     post-render hook. This module loads AFTER order-cards defines
     applyCollapseState and BEFORE main.js calls it, so the wrap catches every
     render (initial expand + post-mutation re-renders). */
  (function wrapApplyCollapse() {
    var orig = window.RQD.applyCollapseState;
    window.RQD.applyCollapseState = function (detailEl, q) {
      var r = (typeof orig === 'function') ? orig.apply(this, arguments) : undefined;
      try { fillHistories(detailEl); } catch (_) {}
      return r;
    };
  })();
})();
